import { classifyComposerSubmit, type IdentityAskState } from '../sdk/identity-ask';
import { IdentityComposerHint, useIdentityDraft } from './IdentityComposer';
/**
 * WebsiteAgentChat (FLO-349) — the visitor-facing chat card for the website
 * agent. A light, warm, Karumi-inspired surface: branded header, greeting +
 * suggested-prompt pills (opener AND follow-up), spoken replies with an explicit
 * "Speak with {name}" mic opt-in, a gated "see it live" CTA, a friendly ended
 * state, and a consent line. Human status copy only — never "Offline".
 *
 * This component FILLS its parent (no fixed positioning) — WebsiteAgentView
 * anchors Q&A to its launcher corner and owns the larger live-demo layouts.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { AgentState, ConnectionProgress, TranscriptMessage } from '../types/agent';
import { getNamedString, getString, type SupportedLanguage } from '../sdk/ui-strings';
import {
  resolveUntaggedOperatorCopy,
  type LanguageOption,
  type PendingLanguageSwitch,
} from '../sdk/language-preference';
import type { PlanProgress } from '../sdk/AgentUI';
import {
  AVATAR_EDGE_BLUR,
  avatarEdgeFade,
  avatarHeroSource,
  chatAvatarSlot,
  chatAvatarStripFrame,
  compactAvatarPresenterSize,
  compactBubbleLift,
  compactBubbleMaxHeight,
  compactBubblePaddingRight,
  compactDockBarPlacement,
  compactDockBarRowStyle,
  compactDockControlSize,
  compactPresenterOverlap,
  compactTypingBarWidth,
  APPROACH_FLIP_MS,
  APPROACH_PLAY_MS,
  approachHoldsStream,
  COMPACT_BUBBLE_PADDING_X_PX,
  COMPACT_DOCK_BAR_BORDER_PX,
  COMPACT_DOCK_BAR_GAP_PX,
  COMPACT_DOCK_BAR_PADDING_PX,
  type AvatarMedia,
  type PresenterBox,
  type CompactDockBar,
  type ViewportTier,
} from './websiteAgentLayout';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { resolveCalendarEmbed } from '../sdk/calendar-embed';
import type { BookingUxHint } from '../sdk/booking-ux-hint';
import { BookingSurface } from './BookingSurface';
import { LauncherMark } from './LauncherMark';
import { LanguageSelector } from './LanguageSelector';
import { AvatarVideo, type AvatarVideoDimensions } from './AvatarVideo';
import { usePresenterFlip } from './useAvatarIntroSequence';

// FLO-383: render the bot's answer markdown safely. `marked` turns markdown into
// HTML; DOMPurify then strips anything executable before it hits the DOM. Bot
// text is grounded in crawled site / KB content, so a prompt-injected
// <script>/onerror/`javascript:` link is plausible — this is the sanitizer the
// codebase otherwise lacks. Links are forced to open in a new tab with noopener.
// Only FINALIZED assistant text is parsed (see Bubble): user input is never
// treated as HTML, and streaming/partial text stays plain to avoid half-formed
// markdown flicker.
marked.setOptions({ breaks: true, gfm: true });
// FLO-383 review: a renderer-local DOMPurify instance so the anchor-attribute
// hook is registered on OUR instance, not the shared module singleton every
// other importer of 'dompurify' would inherit.
const purify = DOMPurify(window);
purify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});
const renderMarkdown = (text: string): string =>
  purify.sanitize(marked.parse(text, { async: false }) as string, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'del', 'code', 'pre', 'ul', 'ol', 'li', 'a', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'hr'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });

interface WebsiteAgentChatProps {
  identityAsk?: IdentityAskState | null;
  onIdentityConfirm?: (value: string) => boolean | void;
  onIdentityReject?: () => void;
  onRequestIdentityAskNow?: (timing: string) => void;
  /**
   * Rendered inside the docked rail (`role="complementary"`, no backdrop, host
   * page fully interactive). Drops the `role="dialog"`/`aria-modal` semantics —
   * they are only true of the floating card, and `aria-modal` on the rail would
   * tell assistive tech the rest of the page is inert when it is not.
   */
  docked?: boolean;
  /**
   * Whether the non-docked surface owns the rest of the page. Anchored desktop
   * Q&A is a non-modal dialog; the mobile full-screen takeover remains modal.
   * Defaults to the legacy behavior (`true` whenever `docked` is false).
   */
  modal?: boolean;
  connected: boolean;
  /**
   * FLO-524: restored across a full page load — the visitor's earlier turns are
   * on screen but NO pipecat session is live behind them (one is booted by the
   * first interaction, so a visitor paging through a site isn't billed a session
   * per pageview). Purely presentational here: it keeps the header honest
   * ("Reconnecting…" would be a lie — there is nothing to reconnect) and keeps
   * the opener chips available as the way back into the conversation.
   */
  dormant?: boolean;
  /** Explicit composer-focus policy. `true` is keyboard-first, `false` is
   *  voice/passive; absent preserves active-session autofocus for onboarding. */
  focusComposerOnOpen?: boolean;
  /** Restore-specific keyboard focus. WebsiteAgentView raises this only when
   *  the visitor expands the compact dock, keeping focus out of the composer
   *  (and avoiding an unsolicited mobile keyboard). */
  focusHeaderOnRestore?: boolean;
  onRestoreFocusApplied?: () => void;
  /**
   * FLO-524: how many of the leading `messages` are RESTORED turns from an
   * earlier page load rather than this session's. Only the identity-capture
   * trigger cares: FLO-325's contract is "after N questions, once per session",
   * and counting restored turns would turn that into "on every page load, before
   * the visitor has said anything" — on a rail with no session behind it, where
   * the submission would be dropped. Everything else (dedup of asked questions,
   * scroll-to-bottom, the empty state) is deliberately blind to the split.
   */
  priorMessageCount?: number;
  /**
   * Resume honesty (FLO-544): the leading `priorMessageCount` turns are
   * restored history the BOT CANNOT SEE — the server-side resume failed or
   * opened a new thread. Renders the "earlier conversation shown for
   * reference — starting fresh" divider under them, so the painted transcript
   * never implies a memory the session doesn't have.
   */
  priorStale?: boolean;
  agentState: AgentState;
  messages: TranscriptMessage[];
  currentTranscript: string;
  currentRole?: 'user' | 'assistant';
  /** Mic (voice input) state — true once the visitor opts into talking. */
  voiceEnabled?: boolean;
  onToggleMute?: () => void;
  /** Keep the mic in the persistent composer only when false. Defaults true for
   *  onboarding callers that rely on the header control. */
  showHeaderMic?: boolean;
  /**
   * FLO-51: the browser refused the microphone. The control stays live (tapping
   * re-asks, which is the only way back once a denial is remembered) but has to
   * SAY so — otherwise a denied user taps a normal-looking mic button and, if
   * the browser remembers the refusal and rejects instantly, gets no feedback
   * at all. The retired panel surfaced this; nothing did until it was passed here.
   */
  micPermissionDenied?: boolean;
  /** A mic gesture failed because no usable input device was available. */
  micUnavailable?: boolean;
  /**
   * FLO-786: ask for microphone permission directly, once the agent's spoken
   * welcome ends. Supplied ONLY for a session the visitor opened with a voice
   * launcher — they already said they wanted to talk, so a card asking them to
   * ask again is a step to miss rather than a choice to make.
   *
   * Resolves false when the browser reached no decision. That is not a denial:
   * WebKit refuses a request made outside a user activation without ever
   * drawing a prompt, and this one is deliberately made seconds after the tap.
   * The invitation below is the fallback, and its button carries a real gesture.
   */
  onRequestMicAfterWelcome?: () => Promise<boolean>;
  /** Bot-voice (speaker) mute, independent from microphone input. */
  speakerMuted?: boolean;
  onToggleSpeaker?: () => void;
  /** Website-Agent-only layout: familiar call controls live in the footer. */
  controlsAtBottom?: boolean;
  onSendText?: (text: string) => void;
  /** Optional lifted composer state. WebsiteAgentView uses this so a draft
   *  survives layout handoffs that must move the chat between rail and corner. */
  composerDraft?: string;
  onComposerDraftChange?: (draft: string) => void;
  /** Passive exit-intent opening. Presence swaps in dedicated turn-zero copy. */
  exitIntent?: { message?: string };
  /** Compact floating presentation: collapse only the conversation body while
   *  preserving the outcome row, composer, and active call controls. */
  minimized?: boolean;
  /** Restore the conversation body from the compact presentation. */
  onRestore?: () => void;
  /** Restore an expanded demo before Escape falls back to minimizing chat. */
  onRestoreDemo?: () => void;
  /** Leave an otherwise-unhandled Escape for the host in forced fullscreen. */
  disableEscapeMinimize?: boolean;
  onMinimize: () => void;
  onEndSession?: () => void;
  /**
   * FLO-51: the opening assistant bubbles. Defaults to the website agent's
   * "ask me anything / I can show you a live demo" pair. The onboarding agent
   * reuses this whole surface but is a different conversation — it is guiding
   * someone through a product they just signed up for, not pitching a demo —
   * so the copy has to be a prop rather than baked into the component.
   */
  greeting?: string[];
  /** FLO-51: composer placeholder. Defaults to "Ask {siteName} anything…". */
  composerPlaceholder?: string;
  /**
   * FLO-51: onboarding-only step counter, rendered under the header. Absent for
   * the website agent, which has no plan behind the conversation.
   */
  planProgress?: PlanProgress;
  /**
   * FLO-51: hard failure banner (the server could not load this site's context
   * and refuses to greet with wrong product info). Onboarding-only today.
   */
  fatalError?: string;
  /**
   * FLO-51: screen share — onboarding-only, and only when the site actually
   * enabled it. Conditional on the HANDLER like mic/speaker above, so the
   * button simply does not exist for the website agent (and for onboarding
   * embeds that never turned screen capture on — it used to render regardless).
   */
  screenShareEnabled?: boolean;
  onToggleScreenShare?: () => void;
  /** Friendly ended state (FLO-349 #1/#10): session torn down / idle-timed-out.
   *  Shows "Chat ended — ask another question" with a one-tap restart. */
  ended?: boolean;
  onRestart?: () => void;
  /**
   * FLO-51: copy overrides for the ended card. The onboarding agent reuses this
   * exact surface for the idle-PAUSED state — same shape (a dead-end plus one
   * button back into the conversation), different words — rather than growing a
   * second full-panel state that would drift from this one.
   */
  endedTitle?: string;
  endedBody?: string;
  endedCta?: string;
  connectionProgress?: ConnectionProgress;
  /** Opener suggestion chips (FLO-307). Falls back to sensible defaults. */
  openerChips?: string[];
  /** Called only when an opener (not a follow-up) is accepted for sending. */
  onSelectOpener?: (copy: string) => void;
  /** FLO-361: server-pushed, conversation-aware follow-up suggestions. When
   *  present after an answer, these REPLACE the old static-opener leftovers as
   *  the follow-up chips — so suggestions stay relevant to what was discussed. */
  followUpOptions?: string[];
  /** FLO-322/349 #7: surface a "see it live" CTA when a demo is available. */
  demoAvailable?: boolean;
  ctaLabel?: string;
  onRequestDemo?: () => void;
  /** Server-owned voice cue paired with the visible on-demo-intent card. */
  onRequestDemoIdentityPrompt?: () => void;
  /** Host-page CTA request, delivered after this surface mounts/opens. */
  externalDemoRequestId?: number;
  onExternalDemoRequestHandled?: (requestId: number) => void;
  /** Changes only when End creates a genuinely new website-agent session. */
  captureSessionId?: number;
  /** Parent-owned latch that survives sidebar/compact presentation remounts. */
  identityAskedSessionRef?: React.MutableRefObject<number | null>;
  /**
   * FLO-786: advances on every session BOOT — including a restart after a
   * connection drop or a failed start, which reuse `captureSessionId`. The
   * one-ask-per-session microphone latches key off this, so a visitor whose
   * session died abnormally still gets asked directly on their next welcome
   * instead of silently falling back to the tap card.
   */
  sessionEpoch?: number;
  /**
   * The site's Cal/Calendly booking URL, already resolved and prefilled by the
   * SDK (`getResolvedCalendarUrl`). Its PRESENCE is the whole gate: every
   * booking affordance on this surface — the header button, the composer chip,
   * the leave-behind on End, the ended card's CTA — renders only when a site
   * has actually configured one, so a visitor is never offered a calendar that
   * opens onto nothing.
   *
   * Passed RAW. {@link ../sdk/calendar-embed} decides what may go in an iframe
   * vs. only in a link, so a hand-edited or stale config value can never reach
   * a `src`/`href` unchecked.
   */
  calendarLink?: string;
  /**
   * The calendar view owns the whole panel body when open. Controlled by the
   * PARENT (WebsiteAgentView) because the session outlives rail, floating and minimized
   * presentations, so a local flag could slam the calendar shut during a
   * presentation change. It is also the landing site for the bot's
   * `show_calendar` push, which arrives at session scope.
   */
  calendarOpen?: boolean;
  /** The parent is resolving a bounded, attributed destination before opening. */
  calendarPending?: boolean;
  onOpenCalendar?: () => void;
  onCloseCalendar?: () => void;
  /** Provider iframe hint for immediate UX only; webhooks own conversion. */
  onBookingUxHint?: (hint: BookingUxHint) => void;
  /**
   * The visitor pressed End and a calendar exists, so the booking offer gets
   * one beat before the session goes away (the demo agent's end-confirm modal,
   * in the shape this surface can carry). Parent-owned because the End it gates
   * is the parent's.
   */
  endConfirmOpen?: boolean;
  onConfirmEnd?: () => void;
  onCancelEnd?: () => void;
  /** Copy for every booking control. Defaults to "Book a call". */
  bookLabel?: string;
  /** FLO-338: optional talking-head avatar as the "Speak with {name}" face. */
  avatarEnabled?: boolean;
  avatarVideoStream?: MediaStream | null;
  /**
   * The live stream's render died AND its poster could not cover for it. The
   * owner drops the stream so the dock falls back to the idle loop (or lets
   * the surface go) instead of framing a blank presenter.
   */
  onAvatarStreamError?: () => void;
  /** Responsive cut-out size for the minimized speaking presenter. */
  compactPresenterSize?: AvatarVideoDimensions;
  /**
   * FLO-754: viewport tier, for the avatar dock's overlap and text lane.
   * Defaults to the desktop tier so existing callers are unchanged.
   */
  tier?: ViewportTier;
  /**
   * FLO-520: the avatar's static media (idle loop + poster) as a SOURCE, not a
   * hardcoded element — while the avatar is enabled, the empty-state hero paints
   * the same asset as the closed launcher tile. An enabled live avatar stream
   * (`avatarVideoStream`) wins over that static source.
   */
  avatarMedia?: AvatarMedia;
  /**
   * The approach intro's one-shot clip, while it is playing: the docked
   * presenter walks up from the far pose as the live session connects. `null`
   * once it has ended (or was never armed) — the dock then shows the close idle
   * loop, and the live stream replaces either the moment it lands.
   */
  avatarApproach?: { url: string; posterUrl: string; fromBox?: PresenterBox | null } | null;
  /** Lets the view measure the docked presenter's box for the intro FLIP. */
  registerPresenterEl?: (el: HTMLElement | null) => void;
  /** The approach played to its end, or could not play: hand over to the close loop. */
  onAvatarApproachEnded?: () => void;
  /** FLO-325: config-gated identity capture (anonymous-first — never gates the
   *  first answer). The server asks at `timing`, once per session. */
  capture?: {
    enabled?: boolean;
    timing?: 'never' | 'on_open' | 'after_n_questions' | 'on_demo_intent';
    afterNQuestions?: number;
    fields?: Array<'email' | 'name' | 'company' | 'role' | 'phone'>;
    askFields?: Array<'email' | 'name' | 'company' | 'role' | 'phone'>;
    requiredFields?: Array<'email' | 'name' | 'company' | 'role' | 'phone'>;
    phoneRegion?: string;
  };
  siteName?: string;
  siteLogoUrl?: string;
  siteFaviconUrl?: string;
  /** Brand accent (#RRGGBB). */
  accent: string;
  languageOptions?: LanguageOption[];
  selectedLanguage?: SupportedLanguage;
  pendingLanguageSwitch?: PendingLanguageSwitch | null;
  languageSwitchError?: string | null;
  languageSwitchDisabled?: boolean;
  onLanguageChange?: (language: SupportedLanguage) => void;
}

// Warm, light Karumi-style palette — a marketing widget, not an operator console.
/**
 * FLO-786: how long the blocked/no-device NOTICE stays on screen.
 *
 * Long enough to read one sentence twice, short enough that it cannot camp on
 * the avatar dock's single bubble slot (which the live caption and the
 * scrollback also use). It is an announcement, not a decision, so it does not
 * wait to be dismissed.
 */
const MIC_NOTICE_LINGER_MS = 10_000;

/**
 * FLO-911: how long the avatar dock waits for a microphone verdict before it
 * shows the visitor the way to type.
 *
 * Sized to the GREETING, because that is what the visitor is doing with the
 * time. The dock opens, the presenter speaks its opening turn, and the
 * automatic permission request (FLO-786) is fired at the end of exactly that
 * turn — the catalogued openers run about six to eight seconds. Nine gives the
 * greeting room to finish and the prompt a beat to be answered, so a visitor
 * who grants promptly never sees the bar move at all; past it, the dock stops
 * waiting and opens the controls so "Type instead" is on screen rather than two
 * taps down. Shorter would fidget under the greeting; much longer and we are
 * back to the dead-looking dock this ticket is about.
 *
 * It is deliberately NOT tied to `agentState`: the case that matters most is
 * the visitor who never triggered a request at all (arrived with the mic off,
 * `Remote mic off` at join), where there is no speech boundary to wait for.
 */
export const MIC_DECISION_GRACE_MS = 9_000;

const C = {
  surface: '#FFFFFF',
  body: '#FBF9F8',
  ink: '#1C1917',
  sub: '#79716B',
  faint: '#B4ADA7',
  line: '#EEEBE8',
  agentBubble: '#F3F0EE',
};

const PhoneHangupIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.57.9-.98.49-1.88 1.12-2.67 1.85a1 1 0 0 1-1.4-.02L.29 13.08a1 1 0 0 1-.03-1.38C3.32 8.8 7.44 7 12 7s8.68 1.8 11.74 4.7a1 1 0 0 1-.03 1.38l-2.47 2.47a1 1 0 0 1-1.4.02 12.63 12.63 0 0 0-2.67-1.85 1 1 0 0 1-.57-.9v-3.1A15.1 15.1 0 0 0 12 9z" fill="currentColor" />
  </svg>
);

/**
 * Keep the visible call-control glyph stable while its button grows to the
 * 44px compact touch target. The wrapper also protects the geometry from host
 * page `button svg` rules — this overlay renders in light DOM.
 */
const ControlGlyph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="floe-wac-control-glyph"
    aria-hidden="true"
    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, flex: '0 0 16px', lineHeight: 0 }}
  >
    {children}
  </span>
);

export const WebsiteAgentChat: React.FC<WebsiteAgentChatProps> = ({
  connected,
  dormant = false,
  focusComposerOnOpen,
  focusHeaderOnRestore = false,
  onRestoreFocusApplied,
  priorMessageCount = 0,
  priorStale = false,
  agentState,
  messages,
  currentTranscript,
  currentRole,
  voiceEnabled,
  onToggleMute,
  showHeaderMic = true,
  micPermissionDenied,
  micUnavailable,
  onRequestMicAfterWelcome,
  speakerMuted,
  onToggleSpeaker,
  controlsAtBottom = false,
  onSendText,
  composerDraft,
  onComposerDraftChange,
  exitIntent,
  minimized = false,
  onRestore,
  onRestoreDemo,
  disableEscapeMinimize = false,
  onMinimize,
  onEndSession,
  greeting,
  composerPlaceholder,
  planProgress,
  fatalError,
  screenShareEnabled,
  onToggleScreenShare,
  ended,
  onRestart,
  endedTitle,
  endedBody,
  endedCta,
  connectionProgress,
  openerChips,
  onSelectOpener,
  followUpOptions,
  demoAvailable,
  ctaLabel,
  onRequestDemo,
  onRequestDemoIdentityPrompt,
  externalDemoRequestId = 0,
  onExternalDemoRequestHandled,
  captureSessionId = 0,
  identityAskedSessionRef,
  identityAsk, onIdentityConfirm, onIdentityReject, onRequestIdentityAskNow,
  sessionEpoch = 0,
  calendarLink,
  calendarOpen,
  calendarPending = false,
  onOpenCalendar,
  onCloseCalendar,
  onBookingUxHint,
  endConfirmOpen,
  onConfirmEnd,
  onCancelEnd,
  bookLabel,
  avatarEnabled,
  avatarVideoStream,
  onAvatarStreamError,
  tier = 'squeeze',
  // Destructured AFTER `tier` on purpose: the default is derived from it, so a
  // caller that passes only one of the two cannot end up with a presenter size
  // the rest of the dock's geometry disagrees with.
  compactPresenterSize = compactAvatarPresenterSize(tier),
  avatarMedia,
  avatarApproach,
  onAvatarApproachEnded,
  registerPresenterEl,
  capture,
  siteName,
  siteLogoUrl,
  siteFaviconUrl,
  accent,
  docked = false,
  modal,
  languageOptions = [],
  selectedLanguage = 'en',
  pendingLanguageSwitch,
  languageSwitchError,
  languageSwitchDisabled = false,
  onLanguageChange,
}) => {
  const [localDraft, setLocalDraft] = useState('');
  const draft = composerDraft ?? localDraft;
  const setDraft = useCallback((next: string) => {
    if (composerDraft === undefined) setLocalDraft(next);
    onComposerDraftChange?.(next);
  }, [composerDraft, onComposerDraftChange]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const restoreFocusRef = useRef<HTMLButtonElement>(null);
  const compactCopyRef = useRef<HTMLDivElement>(null);
  const isModal = modal ?? !docked;
  // The Website Agent distinguishes voice-first from keyboard-first opening:
  // only the latter opts into composer focus (and a mobile soft keyboard).
  // Callers that omit the prop retain the onboarding surface's active-session
  // autofocus, while passive/dormant opens still leave the host page alone.
  useEffect(() => {
    if (!ended && (focusComposerOnOpen ?? !dormant)) composerRef.current?.focus();
  }, [ended, dormant, focusComposerOnOpen]);
  useEffect(() => {
    if (!focusHeaderOnRestore || minimized || !restoreFocusRef.current) return;
    restoreFocusRef.current.focus({ preventScroll: true });
    onRestoreFocusApplied?.();
  }, [focusHeaderOnRestore, minimized, onRestoreFocusApplied]);
  const letter = (siteName?.trim()?.[0] || 'F').toUpperCase();
  const name = siteName?.trim() || 'this product';
  // Dashboard/host copy predates language ownership. Until those fields are
  // explicitly localized, treating them as Hindi merely because Hindi is the
  // active/default locale is unsafe; their known authoring language is English.
  const englishCopy = useCallback(
    <T,>(value: T | null | undefined): T | undefined =>
      resolveUntaggedOperatorCopy(value, selectedLanguage),
    [selectedLanguage],
  );

  // FLO-520/FLO-753: disabled → none; otherwise live stream > configured idle
  // loop > poster > logo > letter.
  const reducedMotion = usePrefersReducedMotion();
  // A dead loop/poster URL falls back to the logo/letter branches below rather
  // than framing a broken media element.
  const [heroMediaFailed, setHeroMediaFailed] = useState(false);
  // The docked presenter starts at the resting tile's size and place and grows
  // into its own while the approach plays (see presenterFlip): below the
  // squeeze tier the two boxes differ by 1.5x and 64px, which without this is
  // a visible jump on the click.
  const dockFlipRef = usePresenterFlip(avatarApproach?.fromBox, !!avatarApproach, APPROACH_FLIP_MS);
  const dockPresenterRef = useCallback(
    (el: HTMLDivElement | null) => {
      dockFlipRef.current = el;
      registerPresenterEl?.(el);
    },
    [dockFlipRef, registerPresenterEl],
  );

  const heroInputs = {
    media: heroMediaFailed ? undefined : avatarMedia,
    avatarEnabled,
    hasLogo: !!(siteLogoUrl || siteFaviconUrl),
    reducedMotion,
  };
  // The live stream waits for the walk-up. A stream that arrived mid-approach
  // used to be impossible (the clip ended at 2.5s, the stream came at 6s);
  // with a longer walk and a faster connect it would cut the presenter from
  // mid-stride to the close pose. Held only while the approach is actually on
  // screen (see approachHoldsStream). The approach always ends: on its last
  // frame, on an error, or on the view's APPROACH_MAX_MS backstop.
  const restingHeroSource = avatarHeroSource({ hasStream: false, ...heroInputs });
  const approachOnScreen = approachHoldsStream({
    approachPlaying: !!avatarApproach,
    dockVisible: minimized && !ended,
    restingSource: restingHeroSource,
  });
  const heroSource = approachOnScreen
    ? restingHeroSource
    : avatarHeroSource({ hasStream: !!avatarVideoStream, ...heroInputs });

  const status = useMemo(() => {
    if (ended) return englishCopy(endedTitle?.trim()) || getString('wa.status.ended');
    // A restored chat has nothing to reconnect to until the visitor engages.
    if (dormant) return getString('wa.status.dormant');
    if (agentState === 'connecting') return connectionProgress?.message || getString('wa.status.connecting');
    switch (agentState) {
      case 'listening': return getString(voiceEnabled ? 'wa.status.listening' : 'wa.status.online');
      case 'thinking': return getString('wa.status.thinking');
      case 'speaking': return getString('wa.status.replying');
      // FLO-51: onboarding-only — the agent is driving the host page. The
      // website agent never reaches this state.
      case 'guiding': return getString('wa.status.guiding');
      case 'paused': return getString('wa.status.paused');
      default: return getString(connected ? 'wa.status.online' : 'wa.status.reconnecting');
    }
  }, [ended, endedTitle, dormant, agentState, connected, voiceEnabled, connectionProgress, englishCopy]);

  const hasConversation = messages.length > 0 || currentTranscript.trim().length > 0;

  // FLO-755/786: the microphone handoff belongs at the end of the opening spoken
  // turn, not permanently in the control rail. Wait for that turn to finish,
  // ask once, and then leave the ordinary icon-only control behind.
  //
  // How it asks depends on how the visitor arrived. A voice launcher already
  // carries the intent, so `onRequestMicAfterWelcome` goes straight to the
  // browser's own permission prompt ('requesting') — one small card fewer to
  // notice and dismiss. Every other start, and any request the browser declines
  // to decide, falls back to the tap invitation ('visible'). A direct mic tap
  // before the handoff consumes it either way: the visitor has already
  // demonstrated that they understand the control.
  const [micInvitePhase, setMicInvitePhase] =
    useState<'waiting' | 'requesting' | 'visible' | 'done'>('waiting');
  const openingSpeechStartedRef = useRef(false);
  const previousAgentStateRef = useRef(agentState);
  const micAttemptedRef = useRef(false);
  // The notice has already had its say for the current attempt. Without this
  // the recovery branch below re-shows it the moment the linger timer retires
  // it — the two would fight, and the notice would never actually leave.
  const micNoticeSpentRef = useRef(false);
  const micFailedRef = useRef(false);
  const sessionMessages = useMemo(
    () => messages.slice(priorMessageCount),
    [messages, priorMessageCount],
  );
  const hasVisitorTurn =
    sessionMessages.some((message) => message.role === 'user') || currentRole === 'user';

  // Read by the automatic request's continuation, which can land long after the
  // render that started it — a visitor who typed meanwhile has moved on, and
  // the fallback invitation must not appear on top of their answer.
  const hasVisitorTurnRef = useRef(hasVisitorTurn);
  // …and the session it belongs to. A permission sheet can stay open for as
  // long as the visitor ignores it; End + re-open in that window would
  // otherwise let a dead session's verdict paint a card on the live one.
  const micRequestSessionRef = useRef(`${captureSessionId}:${sessionEpoch}`);
  // Counts the ASKS, which the session key above cannot: a second request can
  // legitimately start within one session (the latches reset, the visitor
  // retries), and a continuation that is no longer the latest one has been
  // superseded and must not paint anything.
  const micRequestAttemptRef = useRef(0);

  // Either boundary is a genuinely new session: End (captureSessionId) or a
  // fresh boot (sessionEpoch, which also covers the abnormal deaths End misses).
  const micSessionKey = `${captureSessionId}:${sessionEpoch}`;
  useEffect(() => {
    openingSpeechStartedRef.current = false;
    previousAgentStateRef.current = agentState;
    micAttemptedRef.current = false;
    micNoticeSpentRef.current = false;
    micRequestSessionRef.current = micSessionKey;
    setMicInvitePhase('waiting');
  }, [micSessionKey]);


  useEffect(() => {
    const previousAgentState = previousAgentStateRef.current;
    previousAgentStateRef.current = agentState;
    hasVisitorTurnRef.current = hasVisitorTurn;

    // A mic that dies mid-conversation (the visitor revoked permission in the
    // browser) is news again, even if an earlier notice already had its say.
    // Latching this per SESSION would let the mic go quietly dead with nothing
    // but the control's colour to say so.
    const micFailedNow = !!(micPermissionDenied || micUnavailable);
    if (micFailedNow && !micFailedRef.current) micNoticeSpentRef.current = false;
    micFailedRef.current = micFailedNow;

    // The keyboard launcher is already an explicit mode choice; asking that
    // visitor to turn on a mic would contradict the action they just took.
    if (voiceEnabled || focusComposerOnOpen) {
      setMicInvitePhase('done');
      return;
    }
    // A refused or impossible mic needs saying immediately — but only to someone
    // who actually asked for one. FLO-786: this is the NOTICE, not a retry card;
    // the recovery is the browser's own permission UI, which the SDK watches.
    if (
      (micPermissionDenied || micUnavailable)
      && micAttemptedRef.current
      && !micNoticeSpentRef.current
    ) {
      setMicInvitePhase('visible');
      return;
    }
    // 'requesting' is a browser prompt already on screen — never a second one.
    if (micInvitePhase === 'done' || micInvitePhase === 'requesting') return;
    if (dormant || hasVisitorTurn) return;

    if (agentState === 'speaking') {
      openingSpeechStartedRef.current = true;
      return;
    }

    const openingSpeechJustFinished =
      openingSpeechStartedRef.current && previousAgentState === 'speaking';
    // If this surface mounted during the final state update, the finalized
    // assistant turn is the durable proof that the opening speech completed.
    const finalizedOpeningAlreadyPresent =
      !openingSpeechStartedRef.current
      && sessionMessages.some((message) => message.role === 'assistant');

    if (!openingSpeechJustFinished && !finalizedOpeningAlreadyPresent) return;

    // The voice launcher's handoff: the visitor asked to talk, so the only
    // thing still missing is the browser's permission. Ask for it.
    if (onRequestMicAfterWelcome && !micAttemptedRef.current) {
      micAttemptedRef.current = true;
      micNoticeSpentRef.current = false;
      const requestSession = micSessionKey;
      const requestAttempt = ++micRequestAttemptRef.current;
      setMicInvitePhase('requesting');
      void Promise.resolve(onRequestMicAfterWelcome())
        .then((settled) => {
          if (requestSession !== micRequestSessionRef.current) return;
          if (requestAttempt !== micRequestAttemptRef.current) return;
          // Settled = granted (or already resolved by an earlier tap), and
          // `voiceEnabled` closes this out anyway. Otherwise fall back to the
          // invitation — unless the visitor answered in writing in the
          // meantime, which retires the whole question. A verdict that IS a
          // refusal arrives as micPermissionDenied/micUnavailable and swaps in
          // the recovery copy through the branch above.
          if (settled) setMicInvitePhase('done');
          else if (!hasVisitorTurnRef.current) setMicInvitePhase('visible');
          else setMicInvitePhase('done');
        })
        .catch(() => {
          if (requestSession !== micRequestSessionRef.current) return;
          if (requestAttempt !== micRequestAttemptRef.current) return;
          setMicInvitePhase(hasVisitorTurnRef.current ? 'done' : 'visible');
        });
      return;
    }

    setMicInvitePhase('visible');
  }, [
    agentState,
    dormant,
    focusComposerOnOpen,
    hasVisitorTurn,
    micInvitePhase,
    micPermissionDenied,
    micUnavailable,
    onRequestMicAfterWelcome,
    sessionMessages,
    voiceEnabled,
    captureSessionId,
  ]);

  const micInviteVisible =
    micInvitePhase === 'visible'
    && !voiceEnabled && !ended && !dormant && !hasVisitorTurn && !!onToggleMute;
  // FLO-786: the NOTICE gets out of the way by itself.
  //
  // It has nothing to click, so without this it would sit there for the rest of
  // the session — and in the avatar dock it holds the one bubble slot the bot's
  // live caption and the scrollback share, so a visitor who blocked their mic
  // would stop seeing what the agent is saying. Say it once, then leave. The
  // mic control keeps explaining the state for anyone who looks.
  const micNoticeShowing = micInviteVisible && !!(micPermissionDenied || micUnavailable);
  useEffect(() => {
    if (!micNoticeShowing) return;
    const timer = setTimeout(() => {
      micNoticeSpentRef.current = true;
      setMicInvitePhase('done');
    }, MIC_NOTICE_LINGER_MS);
    return () => clearTimeout(timer);
  }, [micNoticeShowing]);

  const handleMicToggle = useCallback(() => {
    micAttemptedRef.current = true;
    micNoticeSpentRef.current = false;
    // Keep recovery guidance visible through an immediate remembered denial;
    // a successful retry will close it when voiceEnabled turns true.
    if (!micPermissionDenied && !micUnavailable) setMicInvitePhase('done');
    onToggleMute?.();
  }, [micPermissionDenied, micUnavailable, onToggleMute]);

  // FLO-545 review: where the LIVE stream renders. The hero owns the empty
  // state; the first message unmounts it, so a live (billed) Anam stream moves
  // to the compact strip above the messages instead of going invisible. Truth
  // table in websiteAgentLayout.chatAvatarSlot; static media never gets the
  // strip — the idle loop/poster stay empty-state only.
  const avatarSlot = chatAvatarSlot({
    hasStream: !!avatarVideoStream,
    // Only when the operator's switch is on: stored media must not leak into
    // the card while the avatar is disabled (the FLO-753 rule).
    // `heroMediaFailed` covers a dead or blocked URL: without it the strip
    // would hold its slot around media that can never paint, which is a blank
    // card rather than a graceful collapse back to the no-avatar layout.
    hasIdleMedia:
      !!avatarEnabled &&
      !heroMediaFailed &&
      !!(avatarMedia?.idleLoopUrl || avatarMedia?.posterUrl),
    hasConversation,
    ended,
  });

  // FLO-545 review: the strip tile adopts the stream's own shape (768×1152
  // portrait for Anam) instead of `cover`-cropping it to a full-width band.
  // Null until `loadedmetadata` reports — and again across a stream swap —
  // which renders chatAvatarStripFrame's 16:9 default.
  const [stripDims, setStripDims] = useState<AvatarVideoDimensions | null>(null);
  // Only a DIFFERENT live stream has an unknown shape. The idle handover
  // (stream -> null -> same stream) is the same presenter, and clearing here
  // would snap the strip to its 16:9 default and back underneath the
  // cross-fade meant to hide that swap.
  const lastLiveStreamIdRef = useRef<string | null>(null);
  useEffect(() => {
    const id = avatarVideoStream?.id ?? null;
    if (id !== null && id !== lastLiveStreamIdRef.current) setStripDims(null);
    if (id !== null) lastLiveStreamIdRef.current = id;
  }, [avatarVideoStream]);

  // FLO-51: the opening bubbles. Falls back to the website agent's pair so the
  // existing callers are byte-identical; the onboarding agent passes its own.
  const greetingLines = useMemo(() => {
    if (exitIntent) {
      return [
        englishCopy(exitIntent.message?.trim()) || getString('wa.exitIntent.greeting1'),
        getString('wa.exitIntent.greeting2'),
      ];
    }
    const configured = (englishCopy(greeting) || []).map((s) => s.trim()).filter(Boolean);
    return configured.length
      ? configured
      : [
          getString('wa.greeting1', { name }),
          getString('wa.greeting2'),
        ];
  }, [exitIntent, greeting, name, englishCopy]);

  // The compact call surface is intentionally not a second transcript. It
  // carries only the presenter and the current (or most recent) assistant turn
  // so minimizing preserves conversational orientation without hiding the host
  // page behind another chat window.
  // The exchange the bubble shows: the turn before it (greyed), the visitor's
  // side (their live speech, or the typed/spoken question the answer follows),
  // and the presenter's answer. The visitor's words used to be dropped here —
  // the bubble simply vanished while they spoke and while their typed turn was
  // pending — so a typed question had no on-screen echo until the answer came.
  const compactExchange = useMemo(() => {
    const streaming = currentRole === 'assistant' && !!currentTranscript.trim();
    const liveQuestion = currentRole === 'user' ? currentTranscript.trim() : '';
    const pending = !streaming && !liveQuestion && messages[messages.length - 1]?.role === 'user';
    // Where the answer sits in history: past the end while it streams or is
    // still owed, else the newest assistant turn.
    let answerIndex = messages.length;
    let answer = streaming ? currentTranscript.trim() : '';
    if (!streaming && !liveQuestion && !pending) {
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        if (messages[i].role === 'assistant' && messages[i].content.trim()) {
          answerIndex = i;
          answer = messages[i].content.trim();
          break;
        }
      }
    }
    // The question is the user turn immediately before the answer (no
    // assistant turn between them); a live user transcript wins outright.
    let question = liveQuestion;
    if (!question) {
      for (let i = answerIndex - 1; i >= 0; i -= 1) {
        if (!messages[i].content.trim()) continue;
        if (messages[i].role === 'user') question = messages[i].content.trim();
        break;
      }
    }
    // The turn BEFORE the exchange, so a visitor who glances away mid-answer
    // still has the thread. Never the same string twice: that would read as a
    // stutter, not as history.
    // Kept while the visitor is still asking (live or owed): it is what they
    // are replying to. Once the answer arrives it replaces it — the visitor's
    // line already carries the thread, and a third block above it pushed the
    // bubble into its scroll cap on every turn.
    let previous = '';
    if (!question || !answer) {
      for (let i = answerIndex - 1; i >= 0; i -= 1) {
        if (messages[i].role === 'assistant' && messages[i].content.trim()) { previous = messages[i].content.trim(); break; }
      }
    }
    if (previous === answer) previous = '';
    if (!answer && !question && !pending && messages.length === 0) answer = greetingLines[0]?.trim() || '';
    return { previous, question, answer, pending };
  }, [currentRole, currentTranscript, messages, greetingLines]);
  const compactAssistantCopy = compactExchange.answer;
  // The compact surface is a live caption, not a second full chat panel. Keep
  // it bounded beside the avatar, but follow the newest spoken line and leave
  // the complete text scrollable instead of hard-clipping it after three lines.
  useEffect(() => {
    const copy = compactCopyRef.current;
    if (!minimized || !copy) return;
    copy.scrollTop = copy.scrollHeight;
    // The presenter's height is a dependency because it is the bubble's CAP: a
    // rotation or a drag across the narrow step re-caps the box mid-answer, and
    // keyed to the copy alone this effect would not re-run — leaving the
    // visitor looking at the middle of a sentence the presenter has passed.
  }, [compactAssistantCopy, minimized, compactPresenterSize.height]);
  // FLO-754: is there a real presenter to build the dock around? This is the
  // ONE gate for the whole redesign — a site with the avatar off, or with no
  // renderable media, keeps the pre-existing compact dock untouched.
  const avatarPresent =
    heroSource === 'stream' || heroSource === 'loop' || heroSource === 'poster';
  const compactAvatarVisible = minimized && !ended && avatarPresent;

  // Which configuration the dock's bar is in. Resets to 'voice' whenever the
  // dock goes away, so re-minimizing never lands the visitor in a composer
  // they opened ten minutes ago.
  const [dockBar, setDockBar] = useState<CompactDockBar>('voice');
  // FLO-911: did the DOCK move the bar, or did the visitor? Only a bar the mic
  // flow opened by itself may be closed by itself — one the visitor opened is
  // theirs to keep, and a late permission grant must not snatch it back.
  const dockBarAutoExpandedRef = useRef(false);
  // The dock has already shown this visitor the way to type during this dock
  // session; it does not keep re-offering over a choice they have made.
  const dockTypingOfferedRef = useRef(false);
  const setDockBarByVisitor = useCallback((next: CompactDockBar) => {
    dockBarAutoExpandedRef.current = false;
    setDockBar(next);
  }, []);
  // FLO-911 review: moving the bar is not the only way to engage with it. A
  // control that exists ONLY in the expanded configuration — the speaker
  // toggle — is the visitor using the thing the dock opened, so a late grant
  // must not close it under their finger. The mic control deliberately does NOT
  // pin the bar: it is in every configuration, and reaching for it is an
  // expression of VOICE intent, so the grant that follows should hand back the
  // voice dock rather than park a listening visitor in controls they never
  // asked for.
  const keepDockBarOpen = useCallback(() => {
    dockBarAutoExpandedRef.current = false;
  }, []);
  const handleDockSpeakerToggle = useCallback(() => {
    keepDockBarOpen();
    onToggleSpeaker?.();
  }, [keepDockBarOpen, onToggleSpeaker]);
  const dockBarPlacement = compactDockBarPlacement(tier);
  const activeIdentity = identityAsk?.status !== 'resolved' ? identityAsk : null;
  const confirmMode = !!activeIdentity && classifyComposerSubmit(activeIdentity.field, draft) === 'confirm';
  const consumeIdentityDraft = useIdentityDraft(identityAsk, draft, setDraft, () => { setDockBarByVisitor('typing'); requestAnimationFrame(() => { composerRef.current?.focus(); composerRef.current?.select(); }); });
  const identityInputProps = activeIdentity ? { inputMode: activeIdentity.field === 'phone' ? 'tel' as const : activeIdentity.field === 'email' ? 'email' as const : 'text' as const, autoComplete: activeIdentity.field === 'phone' ? 'tel' : activeIdentity.field === 'email' ? 'email' : 'off' } : {};

  const [scrollbackOpen, setScrollbackOpen] = useState(false);
  useEffect(() => {
    if (compactAvatarVisible) return;
    dockBarAutoExpandedRef.current = false;
    dockTypingOfferedRef.current = false;
    setDockBar('voice');
    setScrollbackOpen(false);
  }, [compactAvatarVisible]);
  // A text-first start opens straight into the composer: the visitor already
  // said which mode they wanted by tapping the keyboard affordance.
  useEffect(() => {
    if (compactAvatarVisible && focusComposerOnOpen) {
      dockTypingOfferedRef.current = true;
      setDockBarByVisitor('typing');
    }
  }, [compactAvatarVisible, focusComposerOnOpen, setDockBarByVisitor]);

  // ─── FLO-911: the dock's microphone flow ───────────────────────────────────
  //
  // Beside the avatar presenter the dock IS the surface: `composerFooter` is
  // suppressed, so the ONLY text input on the page is the one inside the bar's
  // 'typing' configuration — two taps deep behind "More controls". Visitors who
  // arrived with the mic off were told "You can keep typing" with nothing to
  // type into (2026-09-13 hands-on), and three production sessions in the
  // 09-12→09-15 window joined with `Remote mic off` and produced zero input of
  // any kind. So the bar now answers the mic question by itself:
  //
  //   refused  → the composer, immediately (the notice lands beside a real input)
  //   ignored  → the controls bar, so "Type instead" is one tap away
  //   granted  → today's voice dock, untouched
  //
  // `micPermissionDenied` / `micUnavailable` are the SDK's COMMITTED verdicts.
  // FLO-786 refuses to record a permission-shaped failure the Permissions API
  // has not independently confirmed, because a non-gesture `getUserMedia`
  // rejection is indistinguishable from a real block on WebKit — so a browser
  // that never drew a prompt lands in the "ignored" branch below, not here.
  const micRefused = !!(micPermissionDenied || micUnavailable);
  const micRefusedRef = useRef(micRefused);

  // Review catch: the two latches below are per-SESSION, but they were reset on
  // a different boundary — the dock going away. Those coincide only because
  // every `startSession` in WebsiteAgentView is gated on `!sessionStarted`, and
  // ending a session drops `sessionStarted`, which unmounts the dock. That is a
  // guard in another file holding up an invariant in this one. `micSessionKey`
  // is what this component already calls a session boundary (the mic invite
  // resets on it, deliberately including the abnormal deaths End misses), so
  // key these on it too and the coupling goes away: a new session behind a dock
  // that never unmounted gets its typing offer back, and cannot inherit an
  // auto-expanded flag that would collapse a bar it never opened.
  //
  // Declared ABOVE both effects so React runs it first within one commit, and
  // it deliberately leaves `dockBar` alone — the dock-visibility reset owns the
  // bar's placement, and clobbering it here would fight `focusComposerOnOpen`.
  useEffect(() => {
    dockBarAutoExpandedRef.current = false;
    dockTypingOfferedRef.current = false;
  }, [micSessionKey]);

  useEffect(() => {
    // A mic that dies mid-call is a NEW refusal and earns a fresh offer, the
    // same way the notice itself gets to speak again (see micNoticeSpentRef).
    if (micRefused && !micRefusedRef.current) dockTypingOfferedRef.current = false;
    micRefusedRef.current = micRefused;
    if (!compactAvatarVisible || !onSendText) return;
    if (!micRefused || voiceEnabled) return;
    if (dockTypingOfferedRef.current) return;
    dockTypingOfferedRef.current = true;
    dockBarAutoExpandedRef.current = false;
    setDockBar('typing');
  }, [compactAvatarVisible, micRefused, micSessionKey, onSendText, voiceEnabled]);

  // Nothing decided yet. Open the bar so the keyboard is visible and one tap
  // away — an expansion, not a mode switch, because a prompt sitting unanswered
  // may still be granted a second later.
  useEffect(() => {
    if (!compactAvatarVisible || !onSendText) return;
    if (dockBar !== 'voice' || voiceEnabled || micRefused) return;
    if (dockTypingOfferedRef.current) return;
    const timer = setTimeout(() => {
      dockTypingOfferedRef.current = true;
      dockBarAutoExpandedRef.current = true;
      setDockBar('controls');
    }, MIC_DECISION_GRACE_MS);
    return () => clearTimeout(timer);
    // `micSessionKey`: a new session restarts the clock, so its visitor gets the
    // full grace period rather than whatever was left of the previous one's.
  }, [compactAvatarVisible, dockBar, micRefused, micSessionKey, onSendText, voiceEnabled]);

  // The grant that arrived late. The visitor never asked for the controls, so
  // hand the voice dock back — but only if the bar is still exactly where this
  // flow put it and untouched. Two things clear the flag: `setDockBarByVisitor`
  // (the visitor moved the bar, including into a composer they may be typing
  // into) and `keepDockBarOpen` (the visitor used a control that only exists
  // while the bar is open).
  useEffect(() => {
    if (!voiceEnabled || !dockBarAutoExpandedRef.current) return;
    if (dockBar !== 'controls') return;
    dockBarAutoExpandedRef.current = false;
    setDockBar('voice');
  }, [dockBar, voiceEnabled]);

  const dismissMicInvite = useCallback(() => {
    setMicInvitePhase('done');
    // FLO-911: this button says "Keep typing". In the avatar dock there is
    // nothing to keep typing into until the bar says so, so honour the label —
    // dismissing the invitation IS choosing the keyboard.
    if (compactAvatarVisible && onSendText) {
      dockTypingOfferedRef.current = true;
      setDockBarByVisitor('typing');
    }
  }, [compactAvatarVisible, onSendText, setDockBarByVisitor]);
  // No `dockBarPlacement` dependency, and no focus restore anywhere below: the
  // bar is rendered once and moves by style, so crossing the tier boundary
  // never remounts this field or the controls beside it. Focus, the soft
  // keyboard and any in-flight IME composition simply stay where they are.
  useEffect(() => {
    if (compactAvatarVisible && dockBar === 'typing') composerRef.current?.focus();
  }, [compactAvatarVisible, dockBar]);
  // Scrollback follows the newest turn, the way any transcript should.
  const scrollbackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollbackOpen) return;
    const el = scrollbackRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    // The presenter's height for the same reason the live bubble takes it: it
    // is this box's CAP too, so a resize re-caps an open transcript and,
    // without the dependency, leaves it stranded mid-scroll.
  }, [scrollbackOpen, messages.length, compactPresenterSize.height]);

  // Opener / follow-up suggestions. Research/config owns the marketing copy;
  // the renderer must not invent generic sales questions when none is relevant.
  const openers = useMemo(() => {
    const configured = (englishCopy(openerChips) || []).map((s) => s.trim()).filter(Boolean);
    if (configured.length) return configured.slice(0, 4);
    return demoAvailable ? [getString('wa.showLiveDemo')] : [];
  }, [openerChips, demoAvailable, englishCopy]);

  const asked = useMemo(
    () => new Set(messages.filter((m) => m.role === 'user').map((m) => m.content.trim().toLowerCase())),
    [messages],
  );
  // Follow-up pills after the visitor's turn (not while the agent is working).
  const isVisitorTurn = connected && !ended && (agentState === 'listening' || agentState === 'idle');
  const lastIsAgent = messages.length > 0 && messages[messages.length - 1].role === 'assistant';
  // FLO-382: show the waiting indicator the moment a visitor's question is sent
  // — including during the boot/connect window (agentState 'connecting') and
  // while the LLM is thinking — not only on a spoken turn (which was the sole
  // path that ever set agentState='thinking'). Derived, so it self-clears as
  // soon as the bot starts speaking OR an assistant transcript begins streaming
  // (covers both voice and text-first modes), and stays bounded to a live/
  // connecting session so a drop/end doesn't leave it spinning.
  const awaitingReply =
    !ended &&
    (connected || agentState === 'connecting') &&
    messages.length > 0 &&
    messages[messages.length - 1].role === 'user' &&
    agentState !== 'speaking' &&
    !(currentRole === 'assistant' && currentTranscript.trim().length > 0);
  // FLO-361: follow-up chips are now the SERVER's conversation-aware suggestions
  // (deduped against what's already been asked), NOT the static opener pool minus
  // asked — that old approach just depleted and was never relevant to the answer.
  // Openers are still used for the empty state (turn 0). When the server hasn't
  // sent contextual suggestions (or between turns), we simply show none rather
  // than fall back to stale leftovers.
  const followUpChips = useMemo(
    () =>
      (followUpOptions || [])
        .map((s) => s.trim())
        .filter(Boolean)
        .filter((q) => !asked.has(q.toLowerCase()))
        .slice(0, 3),
    [followUpOptions, asked],
  );
  // FLO-524: a restored chat has a conversation but no live turn behind it, so
  // the server's follow-ups (which arrive per answer) are gone — fall back to
  // the openers. They are the visible affordance that the agent is still usable,
  // and tapping one is what boots the session.
  const showingOpeners = !hasConversation || dormant;
  const suggestionChips = showingOpeners ? openers : followUpChips;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, currentTranscript, hasConversation, agentState]);

  const send = (text: string, opener = false) => {
    const t = text.trim();
    if (!t || !onSendText) return;
    if (opener) onSelectOpener?.(t);
    if (!opener && identityAsk && identityAsk.status !== 'resolved' && classifyComposerSubmit(identityAsk.field, t) === 'confirm' && onIdentityConfirm) {
      if (!connected) return;
      if (onIdentityConfirm(t) === false) return;
      consumeIdentityDraft();
    }
    else onSendText(t);
    setDraft('');
  };

  // FLO-325: config-gated identity capture. Anonymous-first — the card only ever
  // renders AFTER an answer (never gates the first reply), fires once per
  // session at `timing`, and is dismissible (dismiss → continue, no re-fire).
  const captureOn = !!capture?.enabled && capture.timing !== 'never' && !!onRequestIdentityAskNow;
  // THIS page load's completed question/answer pairs only (FLO-524). Restored
  // turns are history the visitor already paid for elsewhere, and a streaming
  // answer is not in `messages` until its lifecycle finishes. Track the number
  // of user turns covered by the latest finalized assistant turn: this makes
  // both count-based timings wait for the corresponding answer instead of
  // competing with it in the composer while it is still streaming.
  const answeredUserTurns = useMemo(() => {
    let userTurns = 0;
    let answeredTurns = 0;
    for (const message of messages.slice(priorMessageCount)) {
      if (message.role === 'user') userTurns += 1;
      else if (!message.id.startsWith('assistant-cue-')) answeredTurns = userTurns;
    }
    return answeredTurns;
  }, [messages, priorMessageCount]);
  const localIdentityAskedSessionRef = useRef<number | null>(null);
  const askedSessionRef = identityAskedSessionRef ?? localIdentityAskedSessionRef;
  useEffect(() => {
    if (!captureOn || !connected || ended || askedSessionRef.current === captureSessionId) return;
    if ((capture?.timing === 'on_open' && answeredUserTurns >= 1) ||
        (capture?.timing === 'after_n_questions' && answeredUserTurns >= (capture.afterNQuestions || 2))) {
      askedSessionRef.current = captureSessionId;
      onRequestIdentityAskNow?.(capture.timing);
    }
  }, [captureOn, connected, ended, capture, answeredUserTurns, onRequestIdentityAskNow, captureSessionId, askedSessionRef]);
  const handleCta = useCallback(() => {
    if (capture?.enabled && capture.timing === 'on_demo_intent') onRequestDemoIdentityPrompt?.();
    onRequestDemo?.();
  }, [capture, onRequestDemoIdentityPrompt, onRequestDemo]);

  const handledExternalDemoRequestRef = useRef(0);
  useEffect(() => {
    if (
      externalDemoRequestId <= 0 ||
      externalDemoRequestId <= handledExternalDemoRequestRef.current
    ) return;
    handledExternalDemoRequestRef.current = externalDemoRequestId;
    handleCta();
    onExternalDemoRequestHandled?.(externalDemoRequestId);
  }, [externalDemoRequestId, handleCta, onExternalDemoRequestHandled]);

  // ─── Booking (FLO-618) ─────────────────────────────────────────────────────
  // One resolution for every booking affordance on this surface. `bookable` is
  // the single gate: no configured URL (or a malformed/insecure one) and the
  // header button, the composer chip, the End leave-behind and the ended card's
  // CTA all simply do not exist — the same "never render a dead CTA" rule
  // `demoAvailable` enforces for the demo handoff.
  const calendar = useMemo(() => resolveCalendarEmbed(calendarLink), [calendarLink]);
  const bookable = calendar.fallbackHref !== null && !!onOpenCalendar;
  const book = englishCopy(bookLabel?.trim()) || getString('wa.bookCall');
  const bookingActionLabel = calendarPending ? getString('wa.status.connecting') : book;
  const voiceRepliesLabel = speakerMuted
    ? getString('wa.voiceRepliesEnable')
    : getString('wa.voiceRepliesMute');
  const compactControlSize = minimized ? 44 : 36;
  // FLO-754 avatar dock. The control size and where the bar can sit are one
  // decision — five 44px controls do not fit across a compact presenter — so both
  // come from the same tier, in websiteAgentLayout. `dockBarPlacement` is
  // declared with the dock's own state above, beside the `dockBar` it pairs with.
  const dockControlSize = compactDockControlSize(tier);
  const micState = voiceEnabled
    ? 'listening'
    : micPermissionDenied
      ? 'blocked'
      : micUnavailable
        ? 'unavailable'
        : 'off';
  const micActionLabel = voiceEnabled
    ? getString('wa.micDisable')
    : micPermissionDenied
      ? getString('wa.micBlocked')
      : micUnavailable
        ? getString('wa.micUnavailable')
        : getString('wa.micEnable');
  // Do not announce the proactive invitation before the spoken welcome ends.
  // The icon remains labelled when focused; the live region speaks only a real
  // state change (listening or failure), while the invite owns its timed ask.
  const micStatus = voiceEnabled
    ? getString('wa.micListening')
    : micPermissionDenied || micUnavailable
      ? micActionLabel
      : '';
  const micColor = micPermissionDenied || micUnavailable
    ? '#B42318'
    : voiceEnabled
      ? '#fff'
      : C.sub;
  const legacyMicTitle = voiceEnabled ? getString('wa.micMuteHint') : micActionLabel;
  // The header button has no filled active background, so its listening color
  // remains the accent while both failure kinds share the existing error red.
  const legacyHeaderMicColor = micPermissionDenied || micUnavailable
    ? '#B42318'
    : voiceEnabled
      ? accent
      : C.sub;
  const renderMicControl = (size: number) => {
    if (!onToggleMute) return null;
    return (
      <button
        type="button"
        onClick={handleMicToggle}
        className="floe-wac-ib floe-wac-control-icon-button"
        aria-label={micActionLabel}
        aria-pressed={!!voiceEnabled}
        title={voiceEnabled ? getString('wa.micMuteHint') : micActionLabel}
        data-floe-mic-state={micState}
        style={{
          ...footerControl(micColor, size),
          width: size,
          minWidth: size,
          padding: 0,
          borderRadius: '50%',
          background: voiceEnabled ? accent : C.body,
          borderColor: voiceEnabled ? accent : micPermissionDenied || micUnavailable ? '#F2B8B5' : C.line,
          boxShadow: micPermissionDenied || micUnavailable ? '0 0 0 2px rgba(180,35,24,0.08)' : undefined,
        }}
      >
        <ControlGlyph>{voiceEnabled ? MicOn : MicOff}</ControlGlyph>
      </button>
    );
  };
  // The bubble's ceiling: the FACE beside it, less the lift that holds the
  // bubble's bottom clear of the presenter's feet. Without subtracting the lift
  // a bubble at its cap tops out a lift's worth ABOVE the face instead of level
  // with it. One value, so the live bubble and the transcript cannot drift.
  const dockBubbleCapPx =
    compactPresenterSize.height - compactBubbleLift(dockBarPlacement, dockBar);
  const dockBubbleStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid ${C.line}`,
    borderRadius: 16,
    // The bubble's BOX runs under the presenter — that overlap is what makes
    // the two read as one object — but its TEXT must not. Hence the asymmetric
    // padding rather than a narrower box.
    padding: `12px ${compactBubblePaddingRight(tier)}px 12px ${COMPACT_BUBBLE_PADDING_X_PX}px`,
    fontSize: 13.5,
    lineHeight: 1.46,
    color: C.ink,
    boxShadow: '0 18px 44px -24px rgba(15,25,40,0.55)',
    display: 'flex',
    flexDirection: 'column',
    gap: 9,
    pointerEvents: 'auto',
    overflowWrap: 'anywhere',
    // A long answer must scroll inside the bubble, not grow it off the top of
    // the viewport — the dock is bottom-anchored, so an unbounded bubble pushes
    // its own beginning off screen. The effect above keeps it on the newest line.
    // ...and it tops out level with the FACE beside it, at whatever size the
    // viewport picked, so the pair keeps reading as one object.
    maxHeight: compactBubbleMaxHeight(tier, dockBubbleCapPx),
    overflowY: 'auto',
    scrollbarWidth: 'thin',
  };
  const renderMicInvite = (placement: 'conversation' | 'avatar' | 'compact') => {
    if (!micInviteVisible) return null;
    // FLO-786: two different things share this slot.
    //
    //  - The INVITATION: the browser reached no decision, so there is still a
    //    permission to win and it needs a tap to carry the user activation.
    //  - The NOTICE: the visitor blocked the mic, or has none. Nothing here can
    //    change that — a retry only re-rejects, and the recovery lives in the
    //    browser's own permission UI, which the SDK now watches. So it says what
    //    is true, once, in the surface's ordinary voice, and stops. No red, no
    //    buttons: refusing a microphone on a marketing page is a choice, not an
    //    error, and the composer beneath it never stopped working.
    const notice = micPermissionDenied
      ? getString('wa.micBlockedNotice')
      : micUnavailable
        ? getString('wa.micUnavailableNotice')
        : null;
    // FLO-911, the invariant this ticket exists for: both notices end with
    // "You can keep typing." Beside the presenter, `composerFooter` is
    // suppressed and the ONLY input on the page is the bar's typing
    // configuration — so a notice rendered over a voice bar is a promise with
    // no input under it, which is exactly the dead end the 09-13 session hit.
    // The effect above puts the bar in 'typing' for every committed refusal;
    // this is the GUARANTEE, not the mechanism. If anything ever routes around
    // that effect, the surface goes quiet (the mic control still carries the
    // blocked state and its label) instead of lying to the visitor.
    if (notice && placement === 'avatar' && dockBar !== 'typing') return null;
    const title = notice ?? getString('wa.micInviteTitle');
    const placementStyle: React.CSSProperties = placement === 'avatar'
      // The bubble's chrome, on a much looser cap. Its button carries the user
      // activation for the permission prompt, so it must not fall under a fold
      // — but a long localized notice still cannot be allowed to grow off the
      // top of the viewport. Far above the bubble's own 36vh, so on any
      // realistic viewport the whole card is visible and this never bites.
      ? { ...dockBubbleStyle, maxHeight: 'min(320px, 60vh)', overflowY: 'auto' }
      : {
          alignSelf: 'flex-start',
          width: placement === 'compact' ? '100%' : 'auto',
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: '10px 11px 10px 13px',
          border: `1px solid ${C.line}`,
          borderRadius: 15,
          background: C.surface,
          boxShadow: '0 12px 28px -20px rgba(28,25,23,0.45)',
        };

    return (
      <div
        className="floe-wac-mic-invite"
        data-floe-mic-invite={notice ? 'notice' : 'ready'}
        // The invitation is a small region with actions in it; the notice is
        // one sentence that the inner live region already announces, so
        // labelling it as a region too just says the same words twice.
        role={notice ? undefined : 'region'}
        aria-label={notice ? undefined : title}
        style={{ ...placementStyle, display: 'flex', flexDirection: 'column', gap: notice ? 0 : 8 }}
      >
        <span role="status" aria-live="polite" aria-atomic="true" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {notice
            ? <span style={{ color: C.ink, fontSize: 12.5, lineHeight: 1.4 }}>{notice}</span>
            : <strong style={{ fontSize: 13, lineHeight: 1.3 }}>{title}</strong>}
        </span>
        {!notice && (
          <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
            <button
              type="button"
              onClick={handleMicToggle}
              className="floe-wac-cta"
              style={{ minHeight: 34, padding: '7px 11px', border: 'none', borderRadius: 999, background: accent, color: '#fff', cursor: 'pointer', fontSize: 11.5, fontWeight: 700 }}
            >
              {getString('wa.micInviteAction')}
            </button>
            <button
              type="button"
              onClick={dismissMicInvite}
              className="floe-wac-ib"
              style={{ minHeight: 34, padding: '7px 9px', border: 'none', borderRadius: 999, background: 'transparent', color: C.sub, cursor: 'pointer', fontSize: 11.5, fontWeight: 650 }}
            >
              {getString('wa.micInviteDismiss')}
            </button>
          </span>
        )}
      </div>
    );
  };
  // The avatar dock's bar. Three configurations, never more than five controls.
  // ONE element in ONE position in the tree, at every tier. Where it sits on
  // screen is entirely its wrapper's style ({@link compactDockBarRowStyle}), so
  // the bar itself owns no positioning at all — moving it must never unmount
  // the composer or the controls inside it.
  const renderDockBar = () => (
    <div
      className="floe-wac-dock-bar"
      role="group"
      aria-label={getString('wa.activeCallControls')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: COMPACT_DOCK_BAR_GAP_PX,
        padding: COMPACT_DOCK_BAR_PADDING_PX,
        border: `${COMPACT_DOCK_BAR_BORDER_PX}px solid ${C.line}`,
        background: C.surface,
        // No `boxShadow` here: the resting shadow lives in the stylesheet below,
        // because an inline style outranks it and the listening glow — which
        // can only be a stylesheet rule, keyed on the dock's data-floe-live —
        // never painted.
        pointerEvents: 'auto',
        boxSizing: 'border-box',
        flexWrap: 'nowrap',
        justifyContent: 'center',
        borderRadius: 999,
        // The only size the bar sets for itself. Voice/controls size to their
        // controls. Typing needs room for the field: at the feet that is the
        // presenter plus its overhang into the lane; in its own row it is the
        // dock's full width, which is the point of the row — beside a compact
        // presenter the feet-pinned pill left the field 96px.
        width: dockBar === 'typing'
          ? (dockBarPlacement === 'feet'
              ? compactTypingBarWidth(compactPresenterSize.width)
              : '100%')
          : undefined,
        // The bar is a flex ITEM of its row, and at the feet that row is only
        // as wide as the presenter. Without this the typing pill — deliberately
        // WIDER than the presenter — is shrunk back to it and the field loses
        // the overhang it exists for. `flex-end` then makes the overflow go
        // left, into the lane, which is where the old absolute `right: 0` put it.
        flexShrink: 0,
      }}
    >
      {dockBar === 'typing' ? (
        <>
          <textarea {...identityInputProps}
            ref={composerRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            wrap="off"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (draft.trim()) send(draft); } }}
            placeholder={activeIdentity ? getString(`identity.placeholder.${activeIdentity.field}`) : englishCopy(composerPlaceholder?.trim()) || getString('wa.followUpPlaceholder')}
            rows={1}
            style={{ boxSizing: 'border-box', minWidth: 0, flex: 1, minHeight: dockControlSize, resize: 'none', border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: C.ink, padding: `0 0 0 11px`, fontFamily: 'inherit', lineHeight: `${dockControlSize}px`, whiteSpace: 'pre', overflowX: 'auto', overflowY: 'hidden' }}
          />
          <button type="button" onClick={() => send(draft)} disabled={!draft.trim()}
            aria-label={confirmMode ? getString('identity.confirm') : getString('wa.send')} className="floe-wac-ib floe-wac-control-icon-button"
            style={{ ...footerControl(draft.trim() ? '#fff' : C.faint, dockControlSize), border: 'none', background: draft.trim() ? accent : C.line, cursor: draft.trim() ? 'pointer' : 'default' }}>
            <ControlGlyph><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg></ControlGlyph>
          </button>
          <button type="button" onClick={() => setDockBarByVisitor('controls')}
            aria-label={getString('wa.backToVoice')} title={getString('wa.backToVoice')}
            className="floe-wac-ib floe-wac-control-icon-button"
            style={{ ...footerControl(C.sub, dockControlSize), background: C.surface }}>
            <ControlGlyph><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg></ControlGlyph>
          </button>
        </>
      ) : (
        <>
          {renderMicControl(dockControlSize)}
          {onEndSession && (
            <button type="button" onClick={onEndSession} className="floe-wac-ib floe-wac-control-icon-button"
              aria-label={getString('wa.endChat')} title={getString('wa.endChat')}
              style={{ ...footerControl('#fff', dockControlSize), borderColor: '#B42318', background: '#B42318' }}>
              <ControlGlyph>{PhoneHangupIcon}</ControlGlyph>
            </button>
          )}
          <button type="button" onClick={() => setDockBarByVisitor(dockBar === 'controls' ? 'voice' : 'controls')}
            aria-label={dockBar === 'controls' ? getString('wa.fewerControls') : getString('wa.moreControls')}
            title={dockBar === 'controls' ? getString('wa.fewerControls') : getString('wa.moreControls')}
            aria-expanded={dockBar === 'controls'}
            className="floe-wac-ib floe-wac-control-icon-button"
            style={{ ...footerControl(C.sub, dockControlSize), background: C.surface }}>
            <ControlGlyph>{dockBar === 'controls' ? <ChevronLeft /> : <ChevronRight />}</ControlGlyph>
          </button>
          {dockBar === 'controls' && onSendText && (
            <button type="button" onClick={() => setDockBarByVisitor('typing')}
              aria-label={getString('wa.typeInstead')} title={getString('wa.typeInstead')}
              className="floe-wac-ib floe-wac-control-icon-button"
              style={{ ...footerControl(C.sub, dockControlSize), background: C.surface }}>
              <ControlGlyph><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="2" y="6" width="20" height="12" rx="2.5" /><path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8" /></svg></ControlGlyph>
            </button>
          )}
          {dockBar === 'controls' && onToggleSpeaker && (
            <button type="button" onClick={handleDockSpeakerToggle} className="floe-wac-ib floe-wac-control-icon-button"
              aria-label={voiceRepliesLabel} title={voiceRepliesLabel}
              style={{ ...footerControl(speakerMuted ? C.faint : accent, dockControlSize), background: C.surface }}>
              <ControlGlyph>{speakerMuted ? SpeakerOff : SpeakerOn}</ControlGlyph>
            </button>
          )}
        </>
      )}
    </div>
  );
  // Escape closes the calendar back to the conversation rather than collapsing
  // the whole panel — the panel-level Escape below would otherwise throw the
  // visitor out of the site entirely from inside a booking flow.
  const showCalendar = !!calendarOpen && calendar.fallbackHref !== null;
  // FLO-911: the dock's bubble slot asks for the CARD, not for the flag. A
  // notice the invariant above withheld must fall through to the live caption
  // rather than blanking the one slot the caption and the scrollback share.
  const avatarMicInvite = compactAvatarVisible ? renderMicInvite('avatar') : null;

  // The conversion/control stack stays mounted while the conversation body
  // collapses. That preserves the draft and call controls, while the consent
  // notice remains owned by the expanded message window instead of following
  // the visitor around as part of the utility rail.
  const composerFooter = (
    <div
      className={[
        controlsAtBottom ? 'floe-wac-control-stack' : '',
        minimized ? 'floe-wac-compact-dock' : '',
      ].filter(Boolean).join(' ') || undefined}
      style={{
        position: 'relative',
        padding: controlsAtBottom
          ? minimized ? '0 6px 6px' : '0 10px 10px'
          : minimized ? '7px 10px 8px' : '10px 14px',
        borderTop: controlsAtBottom ? 'none' : `1px solid ${C.line}`,
        flexShrink: 0,
        background: controlsAtBottom && minimized ? 'transparent' : C.surface,
      }}
    >
      <IdentityComposerHint ask={identityAsk} onReject={() => { consumeIdentityDraft(); onIdentityReject?.(); }} disabled={!connected} />
      {micInviteVisible && minimized && !compactAvatarVisible && (
        <div style={{ marginBottom: 8 }}>{renderMicInvite('compact')}</div>
      )}
      {((demoAvailable && onRequestDemo) || bookable) && (
        <div className="floe-wac-floating-actions" key="outcomes" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', justifyContent: 'center', gap: 7, margin: '0 0 9px' }}>
          {demoAvailable && onRequestDemo && (
            <button type="button" onClick={handleCta} className="floe-wac-chip"
              style={{ flex: controlsAtBottom ? '1 1 128px' : undefined, maxWidth: '100%', minHeight: controlsAtBottom ? minimized ? 44 : 38 : undefined, padding: controlsAtBottom ? '8px 13px' : '7px 14px', borderRadius: 999, border: `1px solid ${controlsAtBottom ? accent : C.line}`, cursor: 'pointer', background: C.surface, color: accent, fontSize: 12.5, fontWeight: 650, lineHeight: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: controlsAtBottom ? '0 8px 22px -14px rgba(28,25,23,0.38)' : undefined }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}><polygon points="5 3 19 12 5 21 5 3" /></svg>
              {englishCopy(ctaLabel?.trim()) || getString('wa.seeLive')}
            </button>
          )}
          {bookable && (
            <button type="button" onClick={onOpenCalendar} disabled={calendarPending}
              aria-busy={calendarPending || undefined}
              className={controlsAtBottom ? 'floe-wac-cta' : 'floe-wac-chip'}
              style={{ flex: controlsAtBottom ? '1 1 128px' : undefined, maxWidth: '100%', minHeight: controlsAtBottom ? minimized ? 44 : 38 : undefined, padding: controlsAtBottom ? '8px 13px' : '7px 14px', borderRadius: 999, border: `1px solid ${controlsAtBottom ? accent : C.line}`, cursor: calendarPending ? 'wait' : 'pointer', opacity: calendarPending ? 0.78 : 1, background: controlsAtBottom ? accent : 'transparent', color: controlsAtBottom ? '#fff' : C.sub, fontSize: 12.5, fontWeight: 650, lineHeight: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: controlsAtBottom ? '0 7px 18px -9px rgba(0,0,0,0.5)' : undefined }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
              {bookingActionLabel}
            </button>
          )}
        </div>
      )}
      {controlsAtBottom ? (
        <div key="control-rail" className="floe-wac-control-rail" style={{ display: 'flex', alignItems: 'center', gap: minimized ? 3 : 5, padding: 4, border: `1px solid ${C.line}`, borderRadius: 999, background: C.surface, boxShadow: '0 12px 30px -16px rgba(28,25,23,0.42)' }}>
          {!ended && renderMicControl(compactControlSize)}
          <div className="floe-wac-composer" style={{ boxSizing: 'border-box', minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 4, background: C.body, border: `1px solid ${C.line}`, borderRadius: 18, padding: minimized ? '3px 3px 3px 8px' : '3px 4px 3px 11px' }}>
            <textarea {...identityInputProps} ref={composerRef} value={draft} onChange={(e) => setDraft(e.target.value)} wrap={minimized ? 'off' : 'soft'}
              onKeyDown={(e) => { if (e.key === 'Enter' && (minimized || !e.shiftKey)) { e.preventDefault(); if (draft.trim()) send(draft); } }}
              placeholder={activeIdentity ? getString(`identity.placeholder.${activeIdentity.field}`) : englishCopy(composerPlaceholder?.trim()) || (!voiceEnabled ? getString('wa.askQuestion') : getNamedString('wa.placeholder', siteName, 'us', 'wa.askQuestion'))} rows={1}
              style={{ boxSizing: 'border-box', minWidth: 0, minHeight: minimized ? 44 : undefined, flex: 1, resize: 'none', border: 'none', outline: 'none', background: 'transparent', fontSize: minimized ? 13 : 13.5, color: C.ink, padding: '8px 0', maxHeight: 110, fontFamily: 'inherit', lineHeight: 1.4, whiteSpace: minimized ? 'pre' : 'pre-wrap', overflowX: minimized ? 'auto' : 'hidden', overflowY: minimized ? 'hidden' : 'auto' }} />
            <button type="button" onClick={() => send(draft)} disabled={!draft.trim()} aria-label={confirmMode ? getString('identity.confirm') : getString('wa.send')} className="floe-wac-control-icon-button"
              style={{ flexShrink: 0, width: minimized ? 44 : 34, height: minimized ? 44 : 34, borderRadius: '50%', border: 'none', cursor: draft.trim() ? 'pointer' : 'default', background: draft.trim() ? accent : C.line, color: draft.trim() ? '#fff' : C.faint, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .15s ease' }}>
              <ControlGlyph><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg></ControlGlyph>
            </button>
          </div>
          {!ended && onToggleSpeaker && (
            <button type="button" onClick={onToggleSpeaker} className="floe-wac-ib floe-wac-control-icon-button"
              aria-label={voiceRepliesLabel} title={voiceRepliesLabel}
              style={{ ...footerControl(speakerMuted ? C.faint : accent, compactControlSize), background: C.surface }}>
              <ControlGlyph>{speakerMuted ? SpeakerOff : SpeakerOn}</ControlGlyph>
            </button>
          )}
          {minimized && onRestore && (
            <button
              key="restore"
              type="button"
              onClick={onRestore}
              data-floe-wa-primary-launcher="true"
              aria-label={getString('wa.returnToCall')}
              title={getString('wa.returnToCall')}
              className="floe-wac-ib floe-wac-control-icon-button"
              style={{ ...footerControl(C.sub, compactControlSize), background: C.surface }}
            >
              <ControlGlyph><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg></ControlGlyph>
            </button>
          )}
          {!ended && onEndSession && (
            <button type="button" onClick={onEndSession} className="floe-wac-ib floe-wac-control-icon-button"
              aria-label={getString('wa.endChat')} title={getString('wa.endChat')}
              style={{ ...footerControl('#fff', compactControlSize), borderColor: '#B42318', background: '#B42318' }}>
              <ControlGlyph>{PhoneHangupIcon}</ControlGlyph>
            </button>
          )}
        </div>
      ) : (
        <div key="composer" style={{ display: 'flex', alignItems: 'flex-end', gap: 8, background: C.body, border: `1px solid ${C.line}`, borderRadius: 16, padding: '5px 6px 5px 16px' }}>
          <textarea {...identityInputProps} ref={composerRef} value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(draft); } }}
            placeholder={activeIdentity ? getString(`identity.placeholder.${activeIdentity.field}`) : englishCopy(composerPlaceholder?.trim()) || getNamedString('wa.placeholder', siteName, 'us', 'wa.askQuestion')} rows={1}
            style={{ flex: 1, resize: 'none', border: 'none', outline: 'none', background: 'transparent', fontSize: 14.5, color: C.ink, padding: '8px 0', maxHeight: 110, fontFamily: 'inherit', lineHeight: 1.4 }} />
          {!ended && onToggleMute && (
            <button type="button" onClick={handleMicToggle}
              aria-label={micActionLabel}
              title={legacyMicTitle}
              data-floe-mic-state={micState}
              style={{ flexShrink: 0, width: 38, height: 38, borderRadius: '50%', border: 'none', cursor: 'pointer', background: voiceEnabled ? accent : 'transparent', color: micColor, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .15s ease, color .15s ease' }}>
              {voiceEnabled ? MicOn : MicOff}
            </button>
          )}
          <button type="button" onClick={() => send(draft)} disabled={!draft.trim()} aria-label={confirmMode ? getString('identity.confirm') : getString('wa.send')}
            style={{ flexShrink: 0, width: 38, height: 38, borderRadius: '50%', border: 'none', cursor: draft.trim() ? 'pointer' : 'default', background: draft.trim() ? accent : C.line, color: draft.trim() ? '#fff' : C.faint, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .15s ease' }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg>
          </button>
        </div>
      )}
      {!controlsAtBottom && (
        // This is the recording disclosure, not decorative helper text. Keep it
        // at the chat's legible small-copy size and contrast in both layouts.
        <div key="consent" style={{ textAlign: 'center', fontSize: 12.5, color: C.sub, marginTop: 9, letterSpacing: '0.01em' }}>
          {getString('wa.consent', { name })}
        </div>
      )}
    </div>
  );

  return (
    <div
      style={{
        width: '100%', height: minimized ? 'auto' : '100%', minHeight: 0,
        display: 'flex', flexDirection: 'column', overflow: minimized ? 'visible' : 'hidden',
        background: minimized ? 'transparent' : C.surface, color: C.ink,
        // FLO-531: this stack overrides the shadow-root base, so Noto Sans
        // Devanagari has to be repeated here or Hindi chat copy falls back to
        // whatever serif the OS picks.
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans Devanagari", sans-serif',
      }}
      role={minimized ? 'group' : docked ? undefined : 'dialog'}
      aria-modal={!minimized && !docked && isModal ? 'true' : undefined}
      aria-label={docked && !minimized ? undefined : getString('wa.dialogLabel', { name })}
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        // Innermost surface first: Escape out of the calendar (or the End
        // confirm) returns to the conversation. Collapsing the whole panel from
        // inside a booking flow would read as losing the booking.
        if (showCalendar) {
          e.stopPropagation();
          onCloseCalendar?.();
        } else if (endConfirmOpen) {
          e.stopPropagation();
          onCancelEnd?.();
        } else if (onRestoreDemo) {
          e.stopPropagation();
          onRestoreDemo();
        } else if (!minimized && !disableEscapeMinimize) {
          e.stopPropagation();
          onMinimize();
        }
      }}
    >
      <style>{`
        @keyframes floe-wac-msg { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        @keyframes floe-wac-dot { 0%,100% { opacity: .35; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-2px); } }
        @keyframes floe-wac-mic-invite { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: none; } }
        .floe-wac-ib { transition: background .14s ease, color .14s ease; }
        .floe-wac-ib:hover { background: rgba(28,25,23,0.06); }
        .floe-wac-control-icon-button { line-height: 0; }
        .floe-wac-control-glyph {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          flex: 0 0 16px;
          line-height: 0;
        }
        .floe-wac-control-glyph > svg {
          display: block;
          width: 16px !important;
          height: 16px !important;
        }
        .floe-wac-mic-invite { animation: floe-wac-mic-invite .22s ease-out both; }
        /* Avatar dock: halo, live glow, listening pulse. */
        .floe-wac-dock-halo {
          position: absolute; z-index: 0; left: -10%; right: -10%; bottom: -4%; height: 78%;
          border-radius: 50%; pointer-events: none; filter: blur(22px); opacity: 0.85;
          background: radial-gradient(ellipse at 50% 70%, color-mix(in srgb, var(--floe-wa-accent) 48%, transparent) 0%, color-mix(in srgb, var(--floe-wa-accent) 22%, transparent) 40%, transparent 72%);
          transition: opacity .6s ease, transform .6s ease;
        }
        .floe-wac-avatar-dock[data-floe-live="speaking"] .floe-wac-dock-halo { opacity: 1; transform: scale(1.12); animation: floe-wac-halo-breathe 2.6s ease-in-out infinite; }
        @keyframes floe-wac-halo-breathe {
          0%, 100% { transform: scale(1.08); opacity: 0.9; }
          50% { transform: scale(1.16); opacity: 1; }
        }
        .floe-wac-dock-bar { transition: box-shadow .35s ease; }
        /* The bar's RESTING shadow. Here rather than inline so the listening
           rule below can win — a style attribute outranks any stylesheet. */
        .floe-wac-dock-bar { box-shadow: 0 14px 34px -18px rgba(15,25,40,0.5); }
        .floe-wac-avatar-dock[data-floe-live="listening"] .floe-wac-dock-bar {
          box-shadow: 0 14px 34px -18px rgba(15,25,40,0.5), 0 0 0 1.5px color-mix(in srgb, var(--floe-wa-accent) 55%, transparent), 0 0 28px color-mix(in srgb, var(--floe-wa-accent) 45%, transparent);
        }
        @keyframes floe-wac-mic-pulse {
          0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--floe-wa-accent) 45%, transparent); }
          60% { box-shadow: 0 0 0 7px transparent; }
        }
        .floe-wac-dock-bar button[data-floe-mic-state="listening"] { animation: floe-wac-mic-pulse 2.2s ease-out infinite; }
        .floe-wac-ib:focus-visible, .floe-wac-chip:focus-visible, .floe-wac-cta:focus-visible {
          outline: 2px solid ${accent};
          outline-offset: 2px;
        }
        .floe-wac-chip { transition: background .14s ease, border-color .14s ease, transform .1s ease, box-shadow .14s ease; }
        .floe-wac-chip:hover { background: #fff; border-color: rgba(28,25,23,0.18); transform: translateY(-1px); box-shadow: 0 4px 14px -6px rgba(28,25,23,0.2); }
        .floe-wac-cta { transition: transform .12s ease, box-shadow .16s ease, filter .16s ease; }
        .floe-wac-cta:hover { transform: translateY(-1px); filter: brightness(1.04); box-shadow: 0 8px 22px -8px rgba(0,0,0,0.4); }
        /* Narrow mobile viewports cannot carry four 44px call controls and a
           useful composer in one row. Keep the mic + composer on row one and
           let the remaining controls wrap, preserving DOM, visual, and focus
           order. */
        @media (max-width: 420px) {
          .floe-wac-compact-dock .floe-wac-control-rail {
            flex-wrap: wrap;
            justify-content: center;
            row-gap: 4px !important;
            border-radius: 24px !important;
          }
          .floe-wac-compact-dock .floe-wac-composer {
            flex: 1 0 calc(100% - 51px) !important;
          }
        }
        /* FLO-383: scoped markdown resets — overlay is LIGHT DOM, so host-page
           CSS can otherwise bleed into rendered bot answers. */
        .floe-wac-md > :first-child { margin-top: 0; }
        .floe-wac-md > :last-child { margin-bottom: 0; }
        /* Customer sites commonly style every paragraph globally. Explicit color is
           required here because greeting/answer markdown contains paragraphs;
           without it a dark host theme can turn light-panel copy white. */
        .floe-wac-md p { margin: 0 0 8px; color: inherit; }
        .floe-wac-md ul, .floe-wac-md ol { margin: 4px 0 8px; padding-left: 20px; }
        .floe-wac-md li { margin: 2px 0; }
        .floe-wac-md a { color: ${accent}; text-decoration: underline; }
        .floe-wac-md code { background: rgba(28,25,23,0.06); border-radius: 4px; padding: 1px 5px; font-size: 0.92em; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .floe-wac-md pre { background: rgba(28,25,23,0.06); border-radius: 8px; padding: 10px 12px; overflow-x: auto; margin: 6px 0; }
        .floe-wac-md pre code { background: none; padding: 0; }
        .floe-wac-md blockquote { margin: 6px 0; padding-left: 10px; border-left: 3px solid ${C.line}; color: ${C.sub}; }
        .floe-wac-md h1, .floe-wac-md h2, .floe-wac-md h3, .floe-wac-md h4 { margin: 8px 0 4px; font-size: 1em; font-weight: 650; }
        .floe-wac-md hr { border: none; border-top: 1px solid ${C.line}; margin: 8px 0; }
        .floe-wac-scroll::-webkit-scrollbar { width: 8px; }
        .floe-wac-scroll::-webkit-scrollbar-thumb { background: rgba(28,25,23,0.12); border-radius: 4px; }
        @media (prefers-reduced-motion: reduce) {
          .floe-wac-ib, .floe-wac-chip, .floe-wac-cta { transition: none !important; }
          .floe-wac-chip:hover, .floe-wac-cta:hover { transform: none; }
          .floe-wac-mic-invite { animation: none !important; }
          .floe-wac-dock-halo, .floe-wac-dock-bar { transition: none !important; animation: none !important; }
          .floe-wac-dock-bar button[data-floe-mic-state="listening"] { animation: none !important; }
        }
      `}</style>

      {!ended && onToggleMute && (
        <span
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}
        >
          {micStatus}
        </span>
      )}

      {/* Header */}
      {!minimized && <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '14px 16px', borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
        <LauncherMark
          siteLogoUrl={siteLogoUrl}
          siteFaviconUrl={siteFaviconUrl}
          letter={letter}
          accent={accent}
          size={36}
          letterSize={16}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 650, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{siteName || 'Floe'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.sub, marginTop: 1 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: ended ? C.faint : connected || dormant ? '#3fb27f' : C.faint, flexShrink: 0 }} />
            {status}
          </div>
        </div>
        {onLanguageChange && (
          <LanguageSelector
            languages={languageOptions}
            value={selectedLanguage}
            pending={pendingLanguageSwitch}
            error={languageSwitchError}
            disabled={languageSwitchDisabled}
            onChange={onLanguageChange}
          />
        )}
        {/* Onboarding keeps its compact legacy header controls. Website Agent
            uses the labelled conversion CTA and familiar call controls in the
            footer, where visitors expect them. */}
        {bookable && !showCalendar && !controlsAtBottom && (
          <button type="button" onClick={onOpenCalendar} disabled={calendarPending}
            aria-busy={calendarPending || undefined} className="floe-wac-ib"
            aria-label={bookingActionLabel} title={bookingActionLabel}
            style={{ ...iconBtn(C.sub), cursor: calendarPending ? 'wait' : 'pointer', opacity: calendarPending ? 0.65 : 1 }}>
            {calendarPending
              ? <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>…</span>
              : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>}
          </button>
        )}
        {/* Mic (talk) — muted by default, so this is the opt-in to speaking.
            (The bot's own voice is on; that's the speaker button below.)
            Kept live behind the calendar: the session does not pause to let
            someone pick a slot, and "does Tuesday work for your team?" is a
            question they should be able to ask while the booker is on screen. */}
        {showHeaderMic && onToggleMute && !ended && (!controlsAtBottom || showCalendar) && (
          <button type="button" onClick={handleMicToggle} className="floe-wac-ib"
            aria-label={micActionLabel}
            title={legacyMicTitle}
            data-floe-mic-state={micState}
            style={iconBtn(legacyHeaderMicColor)}>
            {voiceEnabled ? MicOn : MicOff}
          </button>
        )}
        {/* FLO-51: screen share (onboarding only, and only when configured). */}
        {onToggleScreenShare && !ended && (
          <button type="button" onClick={onToggleScreenShare} className="floe-wac-ib" disabled={!connected}
            aria-label={screenShareEnabled ? 'Stop sharing your screen' : 'Share your screen'}
            title={screenShareEnabled ? 'Stop sharing' : 'Share your screen'}
            style={{ ...iconBtn(screenShareEnabled ? accent : C.sub), cursor: connected ? 'pointer' : 'not-allowed', opacity: connected ? 1 : 0.45 }}>
            {screenShareEnabled ? ScreenShareOn : ScreenShareOff}
          </button>
        )}
        {/* Speaker (bot voice) */}
        {onToggleSpeaker && !ended && (!controlsAtBottom || showCalendar) && (
          <button type="button" onClick={onToggleSpeaker} className="floe-wac-ib" aria-label={voiceRepliesLabel} title={voiceRepliesLabel} style={iconBtn(speakerMuted ? C.faint : accent)}>
            {speakerMuted ? SpeakerOff : SpeakerOn}
          </button>
        )}
        <button ref={restoreFocusRef} type="button" onClick={onMinimize} className="floe-wac-ib" aria-label={getString('wa.minimize')} data-floe-wa-restore-focus-target="true" style={iconBtn(C.sub)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
        {onEndSession && !ended && (!controlsAtBottom || showCalendar) && (
          <button type="button" onClick={onEndSession} className="floe-wac-ib" aria-label={getString('wa.endChat')} style={iconBtn(C.sub)}>
            {PhoneHangupIcon}
          </button>
        )}
      </div>}

      {/* FLO-51: plan progress (onboarding only — the website agent has no plan). */}
      {!minimized && planProgress && !ended && (() => {
        const pct = Number.isFinite(planProgress.progressPercent)
          ? Math.max(0, Math.min(100, planProgress.progressPercent))
          : 0;
        return (
          <div style={{ padding: '9px 16px 10px', borderBottom: `1px solid ${C.line}`, flexShrink: 0, background: C.surface }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: C.sub, marginBottom: 5 }}>
              <span>Step {planProgress.currentStep} of {planProgress.totalSteps}</span>
              <span>{Math.round(pct)}%</span>
            </div>
            <div style={{ height: 3, borderRadius: 2, background: C.line }}>
              <div style={{ height: '100%', width: `${pct}%`, borderRadius: 2, background: accent, transition: 'width 0.5s ease' }} />
            </div>
            {planProgress.currentTaskTitle && (
              <div style={{ fontSize: 11.5, color: C.faint, marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {planProgress.currentTaskTitle}
              </div>
            )}
          </div>
        );
      })()}

      {/* FLO-51: fatal error — the server refused to greet with wrong product info. */}
      {!minimized && fatalError && !ended && (
        <div
          role="alert"
          style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 16px', borderBottom: `1px solid ${C.line}`, background: '#FEF2F2', flexShrink: 0 }}
        >
          <span aria-hidden="true" style={{ fontSize: 13, lineHeight: '18px', flexShrink: 0 }}>⚠️</span>
          <span style={{ fontSize: 12.5, fontWeight: 500, color: '#B42318', lineHeight: '18px' }}>{fatalError}</span>
        </div>
      )}

      {!minimized && (showCalendar ? (
        /* FLO-618: the calendar owns the panel body. The header strip above
           stays (brand, status, mic, speaker) because the session is still
           live — this is a VIEW inside the conversation, not a modal over it,
           which is what lets the visitor keep talking while they pick a slot.

           `calendar.embedSrc` is non-null only for an allowlisted, frame-able
           host; every other configured URL degrades to the button below, which
           opens it in a new tab. Never a blank iframe. */
        <BookingSurface
          calendar={calendar}
          title={book}
          accent={accent}
          onClose={onCloseCalendar}
          onBookingUxHint={onBookingUxHint}
          closeKind="back"
        />
      ) : endConfirmOpen && bookable ? (
        /* FLO-618: the leave-behind. The visitor pressed End, so the session is
           going away either way — this is the last honest moment to offer the
           calendar, and it is the demo agent's end-confirm modal in the shape
           this panel can carry. Without a calendar End still ends immediately
           and nothing about that path changes.

           Review follow-up: `bookable` is in the condition, not just relied on
           upstream. WebsiteAgentView.requestEndSession already gates the same
           way, but that put ONE invariant in TWO components — and if they ever
           drifted the failure was a trap, not a cosmetic bug: the visitor would
           tap "Book a call", openCalendar() would clear endConfirmOpen, the
           calendar branch would refuse an unusable URL, and they would land back
           in a live conversation they believed they had just ended. Gating here
           makes the branch unreachable instead, so End falls through to its
           ordinary path. */
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '24px 28px', background: C.body, textAlign: 'center' }}>
          <span aria-hidden="true" style={{ width: 52, height: 52, borderRadius: '50%', background: C.agentBubble, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
          </span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 650, letterSpacing: '-0.01em' }}>{getString('wa.booking.beforeTitle')}</div>
            <div style={{ fontSize: 13.5, color: C.sub, marginTop: 4, maxWidth: 270 }}>
              {getString('wa.booking.beforeBody', { name })}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, width: '100%', maxWidth: 260, marginTop: 2 }}>
            <button type="button" onClick={onOpenCalendar} disabled={calendarPending}
              aria-busy={calendarPending || undefined} className="floe-wac-cta"
              style={{ padding: '11px 22px', borderRadius: 999, border: 'none', background: accent, color: '#fff', fontSize: 14, fontWeight: 600, cursor: calendarPending ? 'wait' : 'pointer', opacity: calendarPending ? 0.78 : 1, boxShadow: '0 6px 18px -6px rgba(0,0,0,0.35)' }}>
              {bookingActionLabel}
            </button>
            {/* Two ways out, both plainly labelled: back to the conversation,
                or actually end. Neither is a trap — the demo agent's modal makes
                the same promise and this must not be sneakier than that one. */}
            <button type="button" onClick={onCancelEnd} className="floe-wac-chip"
              style={{ padding: '10px 22px', borderRadius: 999, border: `1px solid ${C.line}`, background: C.surface, color: C.ink, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
              {getString('wa.booking.keepChatting')}
            </button>
            <button type="button" onClick={onConfirmEnd}
              style={{ padding: '4px', border: 'none', background: 'transparent', color: C.sub, fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }}>
              {getString('wa.endChat')}
            </button>
          </div>
        </div>
      ) : ended ? (
        /* Friendly ended state — never "Offline". One tap to ask again. */
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '24px 28px', background: C.body, textAlign: 'center' }}>
          <span aria-hidden="true" style={{ width: 52, height: 52, borderRadius: '50%', background: C.agentBubble, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
          </span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 650, letterSpacing: '-0.01em' }}>{englishCopy(endedTitle?.trim()) || getString('wa.ended.title')}</div>
            <div style={{ fontSize: 13.5, color: C.sub, marginTop: 4, maxWidth: 260 }}>
              {englishCopy(endedBody?.trim()) || getString('wa.ended.body', { name })}
            </div>
          </div>
          <button type="button" onClick={onRestart} className="floe-wac-cta" style={{ marginTop: 4, padding: '11px 22px', borderRadius: 999, border: 'none', background: accent, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 6px 18px -6px rgba(0,0,0,0.35)' }}>
            {englishCopy(endedCta?.trim()) || getString('wa.ended.again')}
          </button>
          {/* The ended card is a dead end for everyone who does NOT want to ask
              another question — which, on a marketing site, is most of them.
              A booking is the one action still worth offering there. Quiet
              (hairline, not a second filled pill) so it reads as the secondary
              door it is. */}
          {bookable && (
            <button type="button" onClick={onOpenCalendar} disabled={calendarPending}
              aria-busy={calendarPending || undefined} className="floe-wac-chip"
              style={{ padding: '9px 18px', borderRadius: 999, border: `1px solid ${C.line}`, background: C.surface, color: C.ink, fontSize: 13, fontWeight: 600, cursor: calendarPending ? 'wait' : 'pointer', opacity: calendarPending ? 0.78 : 1 }}>
              {bookingActionLabel}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* FLO-545 review: persistent live-avatar strip. The hero below is
              EMPTY-STATE ONLY — the first message unmounts it, and without this
              slot a live (billed) Anam stream had no render site at all once a
              conversation existed. Docked between the header strips and the
              messages, OUTSIDE the scroller (flexShrink 0 — it must not scroll
              away), and only ever for a LIVE stream: the idle loop/poster never
              earn a persistent slot. */}
          {avatarSlot === 'strip' && (
            <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.line}`, background: C.surface, flexShrink: 0 }}>
              {/* Ratio-derived tile, centered — a full-width box at the strip's
                  fixed height would `cover`-crop the portrait stream to a thin
                  band. Geometry lives in chatAvatarStripFrame (pure, tested). */}
              <div style={{ ...chatAvatarStripFrame(stripDims), borderRadius: 14, overflow: 'hidden', background: C.agentBubble }}>
                <AvatarVideo
                  stream={avatarVideoStream ?? null}
                  // No live track means the idle swap has retired it (or it has
                  // not arrived yet) — hold the slot with the loop rather than
                  // collapsing the strip out of the card.
                  src={avatarVideoStream ? undefined : avatarMedia?.idleLoopUrl}
                  poster={avatarMedia?.posterUrl}
                  reducedMotion={reducedMotion}
                  objectFit="contain"
                  onError={() => {
                    // A failing LIVE track is the SDK's to clear; only retire
                    // the static media this way.
                    if (!avatarVideoStream) setHeroMediaFailed(true);
                  }}
                  onDimensions={(d) =>
                    setStripDims((prev) =>
                      prev && prev.width === d.width && prev.height === d.height ? prev : d,
                    )
                  }
                  style={{
                    display: 'block',
                    // Own GPU layer (FLO-110, the same block AvatarPiP uses):
                    // the message list repaints at streaming-token cadence —
                    // isolate the decoded video so those repaints don't
                    // re-rasterize it.
                    transform: 'translateZ(0)',
                    willChange: 'transform',
                    backfaceVisibility: 'hidden',
                    contain: 'paint',
                  }}
                />
              </div>
            </div>
          )}
          {/* Messages */}
          <div ref={scrollRef} className="floe-wac-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 16px', background: C.body, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* FLO-338 avatar hero — the "Speak with {name}" face (empty state).
                FLO-520: the source is resolved, not hardcoded — the same idle
                loop the enabled launcher tile plays lands here, and an enabled
                live stream wins when one arrives. */}
            {heroSource !== 'none' && !hasConversation && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '4px 0 14px' }}>
                <div style={{ position: 'relative', width: 112, height: 112, overflow: 'visible', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {heroSource === 'stream' ? (
                    <AvatarVideo
                      stream={avatarVideoStream!}
                      poster={avatarMedia?.posterUrl}
                      reducedMotion={reducedMotion}
                      objectFit="contain"
                    />
                  ) : heroSource === 'loop' ? (
                    <AvatarVideo src={avatarMedia?.idleLoopUrl} poster={avatarMedia?.posterUrl} reducedMotion={reducedMotion} objectFit="contain" onError={() => setHeroMediaFailed(true)} />
                  ) : heroSource === 'poster' ? (
                    <AvatarVideo poster={avatarMedia?.posterUrl} reducedMotion objectFit="contain" onError={() => setHeroMediaFailed(true)} />
                  ) : heroSource === 'logo' ? (
                    <LauncherMark
                      siteLogoUrl={siteLogoUrl}
                      siteFaviconUrl={siteFaviconUrl}
                      letter={letter}
                      accent={accent}
                      size={96}
                      letterSize={40}
                    />
                  ) : (
                    <span aria-hidden="true" style={{ color: '#fff', fontSize: 40, fontWeight: 700 }}>{letter}</span>
                  )}
                </div>
                {onToggleMute && !voiceEnabled && !controlsAtBottom && (
                  <button type="button" onClick={handleMicToggle} className="floe-wac-cta"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 999, border: `1px solid ${C.line}`, background: C.surface, color: C.ink, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
                    <span style={{ color: accent }}>{MicOff}</span>
                    {getNamedString('wa.speakWith', siteName, 'us', 'wa.talkToAgent')}
                  </button>
                )}
              </div>
            )}
            {greetingLines.map((line, i) => (
              <Bubble key={`greeting-${i}`} role="assistant" text={line} accent={accent} />
            ))}
            {/* Resume honesty (FLO-544): when the restored turns are history
                the bot cannot see, a divider closes them off — same keys, same
                order, so the split render reconciles as the plain list did. */}
            {messages.slice(0, priorMessageCount).map((m) => <Bubble key={m.id} role={m.role} text={m.content} accent={accent} />)}
            {priorStale && priorMessageCount > 0 && (
              <div role="separator" aria-label={getString('wa.freshStart')} style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0' }}>
                <span aria-hidden="true" style={{ flex: 1, height: 1, background: C.line }} />
                <span style={{ fontSize: 12, color: C.sub, textAlign: 'center', maxWidth: 220 }}>
                  {getString('wa.freshStart')}
                </span>
                <span aria-hidden="true" style={{ flex: 1, height: 1, background: C.line }} />
              </div>
            )}
            {messages.slice(priorMessageCount).map((m) => <Bubble key={m.id} role={m.role} text={m.content} accent={accent} />)}
            {currentTranscript.trim() && currentRole && <Bubble role={currentRole} text={currentTranscript} accent={accent} streaming />}
            {(agentState === 'thinking' || awaitingReply) && <TypingDots />}
            {!minimized && renderMicInvite('conversation')}

            {/* Suggestion pills — openers (empty state) AND follow-ups after a reply. */}
            {(!hasConversation || dormant || (lastIsAgent && isVisitorTurn)) && suggestionChips.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, marginTop: 6 }}>
                {suggestionChips.map((q, i) => (
                  <button key={q} type="button" onClick={() => send(q, showingOpeners)} className="floe-wac-chip"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 999, border: `1px solid ${C.line}`, background: C.surface, color: C.ink, fontSize: 13.5, fontWeight: 500, cursor: 'pointer', animation: `floe-wac-msg 0.4s ease ${0.05 * i}s both` }}>
                    {q}
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.faint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 10 4 15 9 20" /><path d="M20 4v7a4 4 0 0 1-4 4H4" /></svg>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      ))}
      {/* ── FLO-754: the AVATAR dock ────────────────────────────────────────
          One presenter, one bubble, one bar. The presenter holds the right at a
          fixed size and never enters a container; the bubble sits to its left
          and it overlaps the bubble's right end, so the pair reads as one
          object instead of a card with a face in it. The bar is one element in
          one place in the tree; only its wrapper's STYLE changes with the tier
          — see `compactDockBarPlacement` and `compactDockBarRowStyle`.

          Gated entirely on `compactAvatarVisible`. With no renderable avatar the
          old compact dock below is untouched. */}
      {compactAvatarVisible && (
        <div
          className="floe-wac-avatar-dock"
          // What the presenter is doing right now, for the CSS below: the halo
          // brightens while it speaks, the bar and mic glow while it listens.
          data-floe-live={agentState === 'speaking' ? 'speaking' : voiceEnabled ? 'listening' : 'idle'}
          style={{
            ...({ '--floe-wa-accent': accent } as React.CSSProperties),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            width: '100%',
            pointerEvents: 'none',
            // The containing block for the bar row's 'feet' placement. The
            // presenter holds the dock's right edge, so `right: 0` plus the
            // presenter's width lands the row exactly on the face.
            position: 'relative',
          }}
        >
          {/* The [bubble | presenter] pair, always the dock's first row. The
              presenter holds a live <video> and a WebGL chroma-key canvas whose
              context, once lost, does not come back (FLO-754), so an iPad
              rotating across the 1024 breakpoint mid-call must not restructure
              this — and neither this row nor the bar's below it ever does. */}
          <div className="floe-wac-dock-pair" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 10,
                // Clearance for a typing pill that overhangs this lane; see
                // compactBubbleLift.
                paddingBottom: compactBubbleLift(dockBarPlacement, dockBar),
                position: 'relative',
                zIndex: 1,
              }}
            >
              {/* The bubble slot. Identity capture does NOT borrow it: the
                  presenter has just asked for the detail in its own words, and
                  the composer's placeholder names the field — a third copy of
                  the ask ("You can type your name here.") replaced the whole
                  conversation with one orphaned line (2026-09-11 live run). */}
              {avatarMicInvite ? (
                avatarMicInvite
              ) : scrollbackOpen ? (
                <div
                  ref={scrollbackRef}
                  className="floe-wac-dock-bubble floe-wac-dock-scrollback"
                  // The transcript asks for LESS than the face — never more: a
                  // flat 208 stood proud of the narrow (192) and short (132)
                  // frames, which is the very thing the cap exists to stop.
                  style={{ ...dockBubbleStyle, maxHeight: compactBubbleMaxHeight(tier, Math.min(208, dockBubbleCapPx)), overflowY: 'auto', gap: 8 }}
                >
                  {messages.slice(0, priorMessageCount).filter((m) => m.content.trim()).map((m) => (
                    <span key={m.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span lang={m.role === 'user' ? undefined : 'en'} style={{ fontSize: 9.5, letterSpacing: '0.11em', textTransform: 'uppercase', color: C.faint, fontWeight: 700 }}>
                        {m.role === 'user' ? getString('wa.youLabel') : siteName || 'Floe'}
                      </span>
                      {m.content.trim()}
                    </span>
                  ))}
                  {priorStale && priorMessageCount > 0 && (
                    <div role="separator" aria-label={getString('wa.freshStart')} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0' }}>
                      <span aria-hidden="true" style={{ flex: 1, height: 1, background: C.line }} />
                      <span style={{ fontSize: 10.5, color: C.sub, textAlign: 'center', maxWidth: 190 }}>
                        {getString('wa.freshStart')}
                      </span>
                      <span aria-hidden="true" style={{ flex: 1, height: 1, background: C.line }} />
                    </div>
                  )}
                  {messages.slice(priorMessageCount).filter((m) => m.content.trim()).map((m) => (
                    <span key={m.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span lang={m.role === 'user' ? undefined : 'en'} style={{ fontSize: 9.5, letterSpacing: '0.11em', textTransform: 'uppercase', color: C.faint, fontWeight: 700 }}>
                        {m.role === 'user' ? getString('wa.youLabel') : siteName || 'Floe'}
                      </span>
                      {m.content.trim()}
                    </span>
                  ))}
                  <button type="button" onClick={() => setScrollbackOpen(false)} style={dockLinkStyle(accent)}>
                    <ChevronDown />
                    {getString('wa.backToLive')}
                  </button>
                  {/* The recording disclosure. It used to sit under the dock's bar
                      as a permanent caption; the transcript view is where a
                      visitor looks for what the conversation IS, so it lives
                      here — and in the expanded panel — rather than on the
                      presenter's resting frame. */}
                  <span className="floe-wac-chat-consent floe-wac-avatar-consent" style={{ fontSize: 11.5, color: C.sub, textAlign: 'center' }}>
                    {getString('wa.consent', { name })}
                  </span>
                </div>
              ) : compactExchange.answer || compactExchange.question ? (
                <div
                  ref={compactCopyRef}
                  className="floe-wac-dock-bubble"
                  style={dockBubbleStyle}
                >
                  {/* Only the WORDS are the live region. The action row below is a
                      sibling: "Book a call" swaps its label to "Connecting…" while
                      the booking link resolves, and inside the region that swap
                      would be announced as if the presenter had said it. */}
                  <span
                    className="floe-wac-dock-live"
                    role="status"
                    aria-live="polite"
                    aria-atomic="false"
                    style={{ display: 'flex', flexDirection: 'column', gap: 9 }}
                  >
                    {compactExchange.previous && <span style={{ color: C.faint }}>{compactExchange.previous}</span>}
                    {compactExchange.question && (
                      <span className="floe-wac-dock-you" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 9.5, letterSpacing: '0.11em', textTransform: 'uppercase', color: C.faint, fontWeight: 700 }}>
                          {getString('wa.youLabel')}
                        </span>
                        <span style={{ color: C.sub, fontWeight: 500 }}>{compactExchange.question}</span>
                      </span>
                    )}
                    {compactExchange.answer ? (
                      <span>{compactExchange.answer}</span>
                    ) : compactExchange.pending || awaitingReply ? (
                      <TypingDots />
                    ) : null}
                  </span>
                  {(messages.length > 1 || bookable) && (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      {messages.length > 1 ? (
                        <button type="button" onClick={() => setScrollbackOpen(true)} style={dockLinkStyle(accent)}>
                          <ChevronUp />
                          {getString('wa.earlierTurns')}
                        </button>
                      ) : <span />}
                      {/* Booking is an OUTCOME of the conversation, not a call
                          control, so it lives with the words rather than in the
                          bar — which is full at five and wraps at six. It used to
                          be reachable from the dock only by asking the presenter. */}
                      {bookable && (
                        <button type="button" onClick={onOpenCalendar} disabled={calendarPending} aria-busy={calendarPending || undefined}
                          aria-label={bookingActionLabel}
                          style={{ ...dockLinkStyle(accent), cursor: calendarPending ? 'wait' : 'pointer', opacity: calendarPending ? 0.65 : 1 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                          {bookingActionLabel}
                        </button>
                      )}
                    </span>
                  )}
                </div>
              ) : null}

            </div>

            {/* The presenter. `avatarEdgeFade` is applied to an inner layer so it
                masks the video WITHOUT eating the contact shadow that grounds it.
                Where the presenter is wide enough to carry it, the bar rides at
                its feet — the same spot the launcher's "Talk to me" pill
                occupied a moment ago — and the bubble beside it can sit lower;
                where it is not, the bar sits directly beneath the pair, still
                under the face it belongs to. */}
            <div
              ref={dockPresenterRef}
              className="floe-wac-dock-presenter"
              style={{
                position: 'relative',
                flex: `0 0 ${compactPresenterSize.width}px`,
                width: compactPresenterSize.width,
                height: compactPresenterSize.height,
                marginLeft: -compactPresenterOverlap(tier),
                zIndex: 2,
              }}
            >
              {/* The same accent halo the resting launcher wears (LauncherStyles
                  `.floe-wa-avatar-shell::before`), so the presenter is grounded
                  on any page and the call reads as a continuation of the tile,
                  not a different object. Brightens while the presenter speaks. */}
              <span aria-hidden="true" className="floe-wac-dock-halo" />
              <span
                aria-hidden="true"
                style={{ position: 'absolute', left: '20%', right: '20%', bottom: 2, height: 14, borderRadius: '50%', background: 'radial-gradient(50% 50% at 50% 50%, rgba(12,30,50,0.30), transparent 72%)' }}
              />
              <div style={{ position: 'absolute', inset: 0, ...avatarEdgeFade() }}>
                {heroSource === 'stream' ? (
                  <AvatarVideo
                    stream={avatarVideoStream!}
                    poster={avatarMedia?.posterUrl}
                    reducedMotion={reducedMotion}
                    objectFit="contain"
                    edgeBlur={AVATAR_EDGE_BLUR}
                    onError={onAvatarStreamError}
                  />
                ) : heroSource === 'loop' ? (
                  // The approach, then the close loop it lands on — ONE
                  // AvatarVideo, so the hand-over is its ordinary cross-fade.
                  // The live stream takes over from the close loop, never from
                  // mid-approach (approachHoldsStream). A failed approach ends
                  // the approach; it is not evidence against the close loop.
                  <AvatarVideo
                    src={avatarApproach ? avatarApproach.url : avatarMedia?.idleLoopUrl}
                    poster={avatarApproach ? avatarApproach.posterUrl : avatarMedia?.posterUrl}
                    loop={!avatarApproach}
                    playForMs={avatarApproach ? APPROACH_PLAY_MS : undefined}
                    onEnded={avatarApproach ? onAvatarApproachEnded : undefined}
                    // The approach sits over a healthy far poster, so `onError`
                    // (clip AND poster failed) would never report a dead clip.
                    onVideoError={avatarApproach ? onAvatarApproachEnded : undefined}
                    reducedMotion={reducedMotion}
                    objectFit="contain"
                    edgeBlur={AVATAR_EDGE_BLUR}
                    onError={avatarApproach ? onAvatarApproachEnded : () => setHeroMediaFailed(true)}
                  />
                ) : (
                  <AvatarVideo
                    poster={avatarMedia?.posterUrl}
                    reducedMotion
                    objectFit="contain"
                    edgeBlur={AVATAR_EDGE_BLUR}
                    onError={() => setHeroMediaFailed(true)}
                  />
                )}
              </div>

            </div>
          </div>
          {/* The bar's row — ALWAYS the dock's last child, whatever the tier.
              On squeeze its style lifts it out of flow and onto the presenter's
              feet; below that it is a flow row under the pair, right-aligned so
              the pill stays under the face it belongs to (the feet above it are
              already faded by avatarEdgeFade, so it can sit close). The bar
              inside it is rendered exactly once and never changes parents —
              see compactDockBarRowStyle for why that is not negotiable. */}
          <div
            className="floe-wac-dock-bar-row"
            style={compactDockBarRowStyle(dockBarPlacement, dockBar, compactPresenterSize.width)}
          >
            {renderDockBar()}
          </div>
        </div>
      )}
      {!minimized && controlsAtBottom && !showCalendar && !(endConfirmOpen && bookable) && !ended && (
        <div className="floe-wac-chat-consent" style={{ textAlign: 'center', fontSize: 12.5, color: C.sub, padding: '8px 14px 10px', borderTop: `1px solid ${C.line}`, letterSpacing: '0.01em', flexShrink: 0 }}>
          {getString('wa.consent', { name })}
        </div>
      )}
      {!minimized && controlsAtBottom && !showCalendar && !(endConfirmOpen && bookable) && !ended && (
        <div
          className="floe-wac-chat-control-gap"
          aria-hidden="true"
          style={{ height: 14, flexShrink: 0, background: C.body }}
        />
      )}
      {!compactAvatarVisible
        && (minimized || (!showCalendar && !(endConfirmOpen && bookable) && !ended))
        && composerFooter}
    </div>
  );
};

function iconBtn(color: string): React.CSSProperties {
  return { flexShrink: 0, width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'transparent', color, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
}

/** The bubble's own quiet text control ("Earlier" / "Back to live"). */
function dockLinkStyle(accent: string): React.CSSProperties {
  return {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 650,
    letterSpacing: '0.02em',
    color: accent,
  };
}

const chevron = (d: string) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);
const ChevronUp = () => chevron('m18 15-6-6-6 6');
const ChevronDown = () => chevron('m6 9 6 6 6-6');
const ChevronLeft = () => chevron('m15 18-6-6 6-6');
const ChevronRight = () => chevron('m9 18 6-6-6-6');

function footerControl(color: string, size = 36): React.CSSProperties {
  return {
    flexShrink: 0,
    width: size,
    height: size,
    borderRadius: '50%',
    border: `1px solid ${C.line}`,
    color,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

// Memoized (FLO-383 review): the list re-renders at streaming-token cadence, but
// a finalized bubble's props (all primitives) don't change — so memo skips the
// re-render AND the marked+DOMPurify re-parse for every settled message. Only the
// live streaming bubble (changing `text`) actually re-renders.
const Bubble = React.memo<{ role: 'user' | 'assistant'; text: string; accent: string; streaming?: boolean }>(({ role, text, accent, streaming }) => {
  const isUser = role === 'user';
  // Parse markdown ONLY for finalized bot answers. User text is never HTML;
  // streaming text stays plain so a half-typed **bold** doesn't flicker.
  const asMarkdown = !isUser && !streaming;
  const bubbleStyle: React.CSSProperties = {
    maxWidth: '84%', padding: '10px 14px', borderRadius: 18, fontSize: 14.5, lineHeight: 1.5,
    whiteSpace: asMarkdown ? 'normal' : 'pre-wrap', wordBreak: 'break-word',
    background: isUser ? accent : C.agentBubble, color: isUser ? '#fff' : C.ink,
    borderBottomRightRadius: isUser ? 6 : 18, borderBottomLeftRadius: isUser ? 18 : 6,
    opacity: streaming ? 0.92 : 1,
  };
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', animation: 'floe-wac-msg 0.3s ease' }}>
      {asMarkdown ? (
        <div className="floe-wac-md" style={bubbleStyle} dangerouslySetInnerHTML={{ __html: renderMarkdown(text) }} />
      ) : (
        <div style={bubbleStyle}>{text}</div>
      )}
    </div>
  );
});
Bubble.displayName = 'Bubble';

// NOTE (FLO-51): the retired TranscriptSidebar rendered RAG citation chips off
// `TranscriptMessage.sources`, and an earlier revision of this change ported
// them here to avoid "losing" them. That was based on a wrong premise: the
// `bot_citation` message is emitted ONLY by processors/bot_citation_emitter.py,
// which is wired into demo_bot.py — bot_refactored.py (the onboarding pipeline)
// never sends it. So onboarding has never actually shown a chip, and porting
// them would have ADDED an un-designed UI element to the website agent (which
// does run on demo_bot) rather than preserving anything. `sources` stays on the
// type; if we want citations on either surface it should be its own ticket.

const TypingDots: React.FC = () => (
  <div style={{ display: 'flex', gap: 4, padding: '12px 14px', background: C.agentBubble, borderRadius: 18, borderBottomLeftRadius: 6, width: 'fit-content' }}>
    {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: C.faint, animation: `floe-wac-dot 1.1s ease-in-out ${i * 0.15}s infinite` }} />)}
  </div>
);

export const SpeakerOn = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" /></svg>
);
export const SpeakerOff = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>
);
/** FLO-51: screen-share glyphs, matching the mic/speaker pair's 18px stroke set. */
export const ScreenShareOn = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><path d="M8 21h8M12 17v4" /><path d="M12 11V7l3 2-3 2z" fill="currentColor" /></svg>
);
export const ScreenShareOff = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><path d="M8 21h8M12 17v4" /></svg>
);
export const MicOn = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" /></svg>
);
export const MicOff = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23" /><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" /><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23M12 19v4M8 23h8" /></svg>
);
